/**
 * MapFabManager - Cross-Zone Drag-and-Drop FAB System for Map Views
 * 
 * Based on UnifiedFabManager but tailored for FavoritesMapView with:
            bgColor: getFabBackground(ghostColor, ghostModeEnabled ? 'strong' : 'soft'),
 * Features:
 * - Drag FABs between ANY zone via long-press
 * - Reorder within zones
 * - Scrollable zones when many FABs
 * - 60px uniform size with 5px border for glove-friendly tapping
 */

import React, { useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { Box, IconButton, Tooltip, Badge, Typography, Menu, MenuItem, ListItemIcon, ListItemText, Divider, CircularProgress, Snackbar, Alert, Switch } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import LayersIcon from '@mui/icons-material/Layers';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ConstructionIcon from '@mui/icons-material/Construction';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import SettingsIcon from '@mui/icons-material/Settings';
import RouteIcon from '@mui/icons-material/Route';
import FlagIcon from '@mui/icons-material/Flag';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ContrastIcon from '@mui/icons-material/Contrast';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import StarIcon from '@mui/icons-material/Star';
import GroupsIcon from '@mui/icons-material/Groups';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import AirIcon from '@mui/icons-material/Air';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TerrainIcon from '@mui/icons-material/Terrain';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import MasksIcon from '@mui/icons-material/Masks';
import StorefrontIcon from '@mui/icons-material/Storefront';
// Additional facility type icons
import BoltIcon from '@mui/icons-material/Bolt';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import FactoryIcon from '@mui/icons-material/Factory';
import PropaneTankIcon from '@mui/icons-material/PropaneTank';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import ScienceIcon from '@mui/icons-material/Science';
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import HubIcon from '@mui/icons-material/Hub';
import SpeedIcon from '@mui/icons-material/Speed';
import GridViewIcon from '@mui/icons-material/GridView';
import OpacityIcon from '@mui/icons-material/Opacity';
import LandscapeIcon from '@mui/icons-material/Landscape';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RecyclingIcon from '@mui/icons-material/Recycling';
import HealingIcon from '@mui/icons-material/Healing';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import EngineeringIcon from '@mui/icons-material/Engineering';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
// Vehicle icons for user location marker
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import FireTruckIcon from '@mui/icons-material/FireTruck';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ElectricCarIcon from '@mui/icons-material/ElectricCar';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import FlightIcon from '@mui/icons-material/Flight';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SpaceBarIcon from '@mui/icons-material/SpaceBar';
// Truck stop icon (using LocalShippingIcon already imported above)
import { telegram } from '../services/telegram';
import { feedbackService } from '../services/feedback';
import { getCyberColors } from '../theme/cyberColors';

// Converts a hex color to an rgba string with the supplied alpha for flexible FAB overlays
const withAlpha = (hexColor: string, alpha: number): string => {
  const clean = hexColor.replace('#', '');
  const normalized = clean.length === 3
    ? clean.split('').map(char => char + char).join('')
    : clean;
  const base = normalized.slice(0, 6);
  const intval = parseInt(base, 16);
  if (Number.isNaN(intval)) return hexColor;
  const r = (intval >> 16) & 255;
  const g = (intval >> 8) & 255;
  const b = intval & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

type FabIntensity = 'soft' | 'medium' | 'strong';

// Builds a consistent gradient backdrop that keeps dark-mode FABs legible
const makeFabGradient = (
  color: string,
  darkMode: boolean,
  highContrast: boolean,
  intensity: FabIntensity = 'medium',
): string => {
  const baseAlpha = (() => {
    if (highContrast) return intensity === 'strong' ? 0.55 : intensity === 'soft' ? 0.38 : 0.46;
    if (darkMode) return intensity === 'strong' ? 0.45 : intensity === 'soft' ? 0.28 : 0.34;
    return intensity === 'strong' ? 0.32 : intensity === 'soft' ? 0.18 : 0.24;
  })();

  const endAlpha = Math.min(baseAlpha + (darkMode || highContrast ? 0.18 : 0.1), 0.85);
  return `linear-gradient(135deg, ${withAlpha(color, baseAlpha)} 0%, ${withAlpha(color, endAlpha)} 100%)`;
};

interface ToolbarSurface {
  background: string;
  border: string;
  shadow: string;
}

// Ensures toolbar/taskbar chrome maintains legibility across modes
const makeToolbarSurface = (darkMode: boolean, highContrast: boolean): ToolbarSurface => {
  if (highContrast) {
    if (darkMode) {
      return {
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.92) 0%, rgba(20, 20, 20, 0.96) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.32)',
        shadow: '0 18px 36px rgba(0, 0, 0, 0.7)',
      };
    }
    return {
      background: 'linear-gradient(135deg, rgba(252, 252, 253, 0.98) 0%, rgba(226, 232, 240, 0.98) 100%)',
      border: '1px solid rgba(15, 23, 42, 0.35)',
      shadow: '0 18px 32px rgba(15, 23, 42, 0.25)',
    };
  }

  if (darkMode) {
    return {
      background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(30, 41, 59, 0.92) 100%)',
      border: '1px solid rgba(148, 163, 184, 0.28)',
      shadow: '0 16px 32px rgba(2, 6, 23, 0.6)',
    };
  }

  return {
    background: 'linear-gradient(135deg, rgba(226, 232, 240, 0.85) 0%, rgba(203, 213, 225, 0.9) 100%)',
    border: '1px solid rgba(59, 130, 246, 0.22)',
    shadow: '0 14px 28px rgba(148, 163, 184, 0.28)',
  };
};

// ===============================================================
// TYPES
// ===============================================================

export type MapFabZone = 'top' | 'left' | 'right' | 'bottom';

interface ZoneLayout {
  top: string[];
  left: string[];
  right: string[];
  bottom: string[];
  archived: string[];
}

// Mini-FAB popout direction based on zone
export const getMapPopoutDirection = (zone: MapFabZone): 'up' | 'down' | 'left' | 'right' => {
  switch (zone) {
    case 'top': return 'down';
    case 'bottom': return 'up';
    case 'left': return 'right';
    case 'right': return 'left';
  }
};

interface MapFabManagerProps {
  visible: boolean;
  darkMode: boolean;
  highContrast: boolean;
  // Bottom offset when well sheet is open
  bottomOffset?: number;
  // Map style
  mapStyle: string;
  onCycleMapStyle: () => void;
  // Zoom
  onZoomIn: () => void;
  onZoomOut: () => void;
  // Home/Close
  onClose: () => void;
  // Search
  onSearch: () => void;
  // My Location
  onMyLocation: () => void;
  // Settings
  onSettings: () => void;
  // Community Facilities
  onToggleCommunityFacilities?: () => void;
  communityFacilitiesCount?: number;
  // Add Facility
  onToggleAddFacility?: () => void;
  // Weather overlay
  showWeather?: boolean;
  onToggleWeather?: () => void;
  // Temperature FAB
  temperature?: number | null;
  temperatureColor?: string;
  feelsLike?: number | null;
  // UV Index FAB
  uvIndex?: number | null;
  uvColor?: string;
  // Wind indicators
  showWind?: boolean;
  windSpeed?: number | null;
  onToggleWind?: () => void;
  // Precipitation/Rain FAB - critical for flash flood warnings in desert
  precipProbability?: number | null;
  precipColor?: string;
  thunderstormProbability?: number | null;
  // Air Quality widget
  showAirQuality?: boolean;
  aqiValue?: number | null;
  aqiColor?: string;
  onToggleAirQuality?: () => void;
  // Pollen widget
  showPollen?: boolean;
  pollenIndex?: number | null;
  pollenColor?: string;
  onTogglePollen?: () => void;
  // Nearby Services widget
  showNearbyServices?: boolean;
  onToggleNearbyServices?: () => void;
  // Elevation widget
  showElevation?: boolean;
  onToggleElevation?: () => void;
  hasElevationPath?: boolean;
  // Routes toggle
  showRoutes: boolean;
  isLoadingRoutes: boolean;
  onToggleRoutes: () => void;
  routeCount: number;
  // Landmark mode
  isLandmarkMode: boolean;
  onToggleLandmarkMode: () => void;
  // Facility layer cycling
  currentFacilityLayer: string;
  onCycleFacilityLayer: () => void;
  isLoadingFacilityLayer?: boolean;
  facilityLayerIcon?: ReactNode;
  // Truck stops toggle - separate from facility filters
  showTruckStops?: boolean;
  onToggleTruckStops?: () => void;
  truckStopCount?: number;
  isLoadingTruckStops?: boolean;
  // Heavy haul mode - oversized/hazmat routing
  heavyHaulMode?: boolean;
  onToggleHeavyHaul?: () => void;
  // OCD Activity Layer - incidents, pits, frac activity - COMMENTED OUT
  // showOcdActivity?: boolean;
  // onToggleOcdActivity?: () => void;
  // ocdActivityCount?: number;
  // isLoadingOcdActivity?: boolean;
  // Caliche Routes Layer - community landmark routes
  showCalichemRoutes?: boolean;
  onToggleCalichemRoutes?: () => void;
  calichemRoutesCount?: number;
  isLoadingCalichemRoutes?: boolean;
  // Global Route Photos gallery
  onOpenRoutePhotos?: () => void;
  routePhotosCount?: number;
  // Voice & manual search parity with voice navigation
  voiceStatus?: 'idle' | 'listening' | 'processing' | 'success' | 'error';
  onVoiceClick?: () => void;
  // onManualSearchClick?: () => void;
  aiSpeedDial?: ReactNode;
  // Location sharing controls
  personalLocationEnabled?: boolean;
  crewSharingEnabled?: boolean;
  locationStatus?: 'active' | 'pending' | 'denied' | 'off';
  onTogglePersonalLocation?: () => void;
  onToggleCrewSharing?: () => void;
  onRequestLocation?: () => Promise<{ lat: number; lng: number } | null | void>;
  // Crew membership info - determines if user can toggle crew sharing
  isCrewMember?: boolean;       // Is user a member of ANY crew (not owner)
  isCrewOwner?: boolean;        // Is user an owner of a crew
  crewName?: string;            // Name of crew they're a member of
  // Optional: pass-through components (LandmarkFAB, RouteCreationFAB)
  landmarkFab?: ReactNode;
  routeCreationFab?: ReactNode;
  // Vehicle selector
  currentVehicle?: string;
  onOpenVehicleMenu?: () => void;
  // Settings mini-FAB actions
  onOpenOfflineCache?: () => void;
  onToggleDarkMode?: () => void;
  onToggleHighContrast?: () => void;
  soundEnabled?: boolean;
  onToggleSound?: () => void;
  // Road hazard and cop reporting FABs
  onReportHazard?: () => void;
  onReportCop?: () => void;
  onReportSafetyGuy?: () => void;
  onReportPoop?: () => void;
  poopPinCount?: number;
  onReportTaco?: () => void;
  tacoPinCount?: number;
  // Ghost mode - incognito location sharing
  ghostModeEnabled?: boolean;
  onToggleGhostMode?: () => void;
  // D-pad map panning - now rendered as FABs (dpadUp, dpadDown, dpadLeft, dpadRight)
  onPan?: (direction: 'up' | 'down' | 'left' | 'right') => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_KEY = 'map_fab_zone_layout';
const TOOLBAR_VISIBILITY_KEY = 'map_toolbar_visibility';
const LAYOUT_VERSION = 32; // v32: promote Add Facility FAB to favorites map toolbar
const FAB_SIZE = 64;
const ICON_SIZE = 32;
const LARGE_FAB_SIZE = 84;
const LARGE_ICON_SIZE = 42;
const MAX_FAB_SIZE = Math.max(FAB_SIZE, LARGE_FAB_SIZE);
const TOOLBAR_STRIP_HEIGHT = MAX_FAB_SIZE + 18;
const TOOLBAR_COLUMN_WIDTH = MAX_FAB_SIZE + 24;
const VERTICAL_ZONE_TOP_OFFSET = TOOLBAR_STRIP_HEIGHT + 6;
const VERTICAL_ZONE_BOTTOM_OFFSET = TOOLBAR_STRIP_HEIGHT + 18;
const ARROW_INDICATOR_SIZE = 56;
const ARROW_ICON_SIZE = 32;
const DIRECTIONAL_FAB_IDS = new Set<string>(['dpadUp', 'dpadDown', 'dpadLeft', 'dpadRight', 'zoomIn', 'zoomOut']);
const FAB_BORDER_WIDTH = 5;
const LONG_PRESS_DURATION = 500;
const SWIPE_THRESHOLD = 50; // px to trigger hide/show
const EDGE_ZONE = 25; // px from edge to detect swipe-to-show gesture

const makeBadgeSx = (color: string) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#f8fafc',
    color: '#0a0a0a',
    fontSize: '0.9rem',
    fontWeight: 900,
    fontFamily: 'monospace',
    minWidth: 28,
    height: 24,
    borderRadius: 12,
    border: `2px solid ${color}`,
    boxShadow: `0 0 10px ${color}70, 0 2px 8px rgba(0,0,0,0.4)`,
    padding: '0 10px',
    top: -6,
    right: -6,
  },
});

// Default layout for map FABs
const DEFAULT_LAYOUT: ZoneLayout = {
  top: ['home', 'rain', 'temperature', 'wind', 'uvIndex', 'airQuality', 'pollen', 'dpadUp'],
  left: ['heavyHaul', 'services', 'calichemRoutes', 'routePhotos', 'elevation', 'landmark', 'dpadLeft'],
  right: ['search', 'ai', 'layers', 'myLocation', 'facilityLayer', 'dpadRight', 'zoomIn', 'zoomOut'],
  bottom: ['roadHazard', 'copAlert', 'safetyGuy', 'poop', 'taco', 'addFacility', 'trash', 'settings', 'dpadDown'],
  archived: ['routesToggle', 'gpsToggle', 'truckStops', 'ocdActivity', 'voice', 'ghost', 'vehicle', 'favorites'],
};

// Default toolbar visibility
interface ToolbarVisibility {
  top: boolean;
  left: boolean;
  right: boolean;
  bottom: boolean;
}

const DEFAULT_VISIBILITY: ToolbarVisibility = {
  top: true,
  left: true,
  right: true,
  bottom: true,
};

// ============================================================================
// COMPONENT
// ============================================================================

export function MapFabManager(props: MapFabManagerProps) {
  const {
    visible,
    darkMode,
    highContrast,
    // bottomOffset removed - bottom toolbar is now fixed height
    mapStyle,
    onCycleMapStyle,
    onZoomIn,
    onZoomOut,
    onClose,
    onSearch,
    onMyLocation,
    onSettings,
    onToggleCommunityFacilities,
    communityFacilitiesCount = 0,
    onToggleAddFacility,
    showWeather = true,
    onToggleWeather,
    temperature = null,
    temperatureColor: _temperatureColor = '#94a3b8',
    feelsLike = null,
    uvIndex = null,
    uvColor: _uvColor = '#94a3b8',
    showWind = false,
    windSpeed = null,
    onToggleWind,
    precipProbability = null,
    precipColor: _precipColor = '#94a3b8',
    thunderstormProbability = null,
    showAirQuality: _showAirQuality = true,
    aqiValue = null,
    aqiColor: _aqiColor = '#94a3b8',
    onToggleAirQuality,
    showPollen = true,
    pollenIndex = null,
    pollenColor: _pollenColor = '#94a3b8',
    onTogglePollen,
    showNearbyServices = true,
    onToggleNearbyServices,
    showElevation = false,
    onToggleElevation,
    hasElevationPath = false,
    showRoutes,
    isLoadingRoutes,
    onToggleRoutes,
    routeCount,
    isLandmarkMode,
    onToggleLandmarkMode,
    currentFacilityLayer,
    onCycleFacilityLayer,
    isLoadingFacilityLayer = false,
    facilityLayerIcon,
    showTruckStops = false,
    onToggleTruckStops,
    truckStopCount = 0,
    isLoadingTruckStops = false,
    heavyHaulMode = false,
    onToggleHeavyHaul,
    // OCD Activity - COMMENTED OUT
    // showOcdActivity = false,
    // onToggleOcdActivity,
    // ocdActivityCount = 0,
    // isLoadingOcdActivity = false,
    showCalichemRoutes = false,
    onToggleCalichemRoutes,
    calichemRoutesCount = 0,
    isLoadingCalichemRoutes = false,
    landmarkFab,
    routeCreationFab,
    currentVehicle = 'dot',
    onOpenVehicleMenu,
    onOpenOfflineCache,
    onToggleDarkMode,
    onToggleHighContrast,
    soundEnabled = true,
    onToggleSound,
    voiceStatus = 'idle',
    onVoiceClick,
    // onManualSearchClick,
    aiSpeedDial,
    personalLocationEnabled = false,
    crewSharingEnabled = false,
    locationStatus = 'off',
    onTogglePersonalLocation,
    onToggleCrewSharing: _onToggleCrewSharing,
    onRequestLocation,
    isCrewMember = false,
    isCrewOwner: _isCrewOwner = false,
    crewName,
    // Road hazard and cop reporting
    onReportHazard,
    onReportCop,
    onReportSafetyGuy,
    onReportPoop,
    poopPinCount = 0,
    onReportTaco,
    tacoPinCount = 0,
    // Ghost mode - incognito location sharing
    ghostModeEnabled = false,
    onToggleGhostMode,
    onOpenRoutePhotos,
    routePhotosCount = 0,
    // D-pad map panning - now rendered as FABs
    onPan,
  } = props;

  const [layout, setLayout] = useState<ZoneLayout>(DEFAULT_LAYOUT);
  const [draggedFab, setDraggedFab] = useState<string | null>(null);
  const [dragOverZone, setDragOverZone] = useState<MapFabZone | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDragMode, setIsDragMode] = useState(false);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [trashMenuAnchor, setTrashMenuAnchor] = useState<HTMLElement | null>(null);
  const [isOverTrash, setIsOverTrash] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showGpsMenu, setShowGpsMenu] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });
  const [evenTopBottomSpacing, setEvenTopBottomSpacing] = useState<boolean>(() => {
    const saved = localStorage.getItem('map_fab_even_spacing');
    return saved ? saved === 'true' : false;
  });
  
  // Toolbar visibility state - swipe to hide/show
  const [toolbarVisibility, setToolbarVisibility] = useState<ToolbarVisibility>(DEFAULT_VISIBILITY);
  
  // Tron Light Cycle entrance animation state
  const [fabsEntered, setFabsEntered] = useState(false);
  
  // Swipe gesture tracking refs
  const swipeStartRef = useRef<{ x: number; y: number; zone: MapFabZone | 'edge' | null; edge?: MapFabZone } | null>(null);

  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const fabRefs = useRef<Map<string, HTMLElement>>(new Map());

  const cyber = getCyberColors(darkMode, highContrast);

  const arrowAccents = useMemo(() => {
    const themeColors = getCyberColors(darkMode, highContrast);
    const palette = [
      themeColors.neonCyan,
      themeColors.neonGreen,
      themeColors.neonPurple,
      themeColors.neonPink,
      themeColors.neonYellow,
      themeColors.neonBlue,
      themeColors.neonOrange,
      themeColors.neonRed,
    ].filter(Boolean);
    const uniquePalette = Array.from(new Set(palette));
    if (uniquePalette.length === 0) {
      return {
        top: '#38bdf8',
        bottom: '#22c55e',
        left: '#a855f7',
        right: '#f97316',
      };
    }
    const shuffled = [...uniquePalette].sort(() => Math.random() - 0.5);
    const pick = (index: number) => shuffled[index % shuffled.length];
    return {
      top: pick(0),
      bottom: pick(1),
      left: pick(2),
      right: pick(3),
    };
  }, [darkMode, highContrast]);
  const toolbarSurface = useMemo(() => makeToolbarSurface(darkMode, highContrast), [darkMode, highContrast]);
  const getFabBackground = useCallback(
    (color: string, intensity: FabIntensity = 'medium') => makeFabGradient(color, darkMode, highContrast, intensity),
    [darkMode, highContrast],
  );
  const closeSettingsMenu = useCallback(() => setShowSettingsMenu(false), []);
  const locationStateColor = locationStatus === 'active'
    ? '#10b981'
    : locationStatus === 'pending'
      ? '#f59e0b'
      : locationStatus === 'denied'
        ? '#ef4444'
        : '#6b7280';

  useEffect(() => {
    if (!visible) {
      closeSettingsMenu();
      setShowGpsMenu(false);
    }
  }, [closeSettingsMenu, visible]);

  useEffect(() => {
    if (isDragMode) {
      closeSettingsMenu();
      setShowGpsMenu(false);
    }
  }, [closeSettingsMenu, isDragMode]);

  useEffect(() => {
    if (!showSettingsMenu) return;
    const hasSettingsFab = layout.top.includes('settings') ||
      layout.bottom.includes('settings') ||
      layout.left.includes('settings') ||
      layout.right.includes('settings');
    if (!hasSettingsFab) {
      closeSettingsMenu();
    }
  }, [closeSettingsMenu, layout, showSettingsMenu]);

  // ============================================================================
  // TOOLBAR VISIBILITY PERSISTENCE
  // ============================================================================
  
  useEffect(() => {
    const saved = localStorage.getItem(TOOLBAR_VISIBILITY_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as ToolbarVisibility;
        setToolbarVisibility(parsed);
      } catch (e) {
        console.error('Failed to parse toolbar visibility:', e);
      }
    }
  }, []);
  
  const saveToolbarVisibility = useCallback((newVisibility: ToolbarVisibility) => {
    localStorage.setItem(TOOLBAR_VISIBILITY_KEY, JSON.stringify(newVisibility));
    setToolbarVisibility(newVisibility);
  }, []);
  
  // Toggle toolbar visibility
  const toggleToolbar = useCallback((zone: MapFabZone, show?: boolean) => {
    setToolbarVisibility(prev => {
      const newState = { ...prev, [zone]: show !== undefined ? show : !prev[zone] };
      localStorage.setItem(TOOLBAR_VISIBILITY_KEY, JSON.stringify(newState));
      telegram.hapticFeedback('light');
      return newState;
    });
  }, []);
  
  // Show all toolbars
  const showAllToolbars = useCallback(() => {
    saveToolbarVisibility(DEFAULT_VISIBILITY);
    telegram.hapticFeedback('success');
  }, [saveToolbarVisibility]);
  
  // Check if any toolbar is hidden
  const anyToolbarHidden = !toolbarVisibility.top || !toolbarVisibility.left || 
                           !toolbarVisibility.right || !toolbarVisibility.bottom;

  // ============================================================================
  // SWIPE GESTURE HANDLERS
  // ============================================================================
  
  // Detect which edge the touch started from (for showing hidden toolbars)
  const detectEdge = useCallback((x: number, y: number): MapFabZone | null => {
    const { innerWidth, innerHeight } = window;
    if (y < EDGE_ZONE) return 'top';
    if (y > innerHeight - EDGE_ZONE) return 'bottom';
    if (x < EDGE_ZONE) return 'left';
    if (x > innerWidth - EDGE_ZONE) return 'right';
    return null;
  }, []);
  
  // Handle touch start on edge zones (for showing hidden toolbars)
  const handleEdgeTouchStart = useCallback((e: TouchEvent) => {
    // Don't interfere with FAB dragging
    if (isDragMode || draggedFab) return;
    
    const touch = e.touches[0];
    const edge = detectEdge(touch.clientX, touch.clientY);
    
    if (edge && !toolbarVisibility[edge]) {
      // Touch started at edge where toolbar is hidden - track for swipe to show
      swipeStartRef.current = { x: touch.clientX, y: touch.clientY, zone: 'edge', edge };
    }
  }, [detectEdge, isDragMode, draggedFab, toolbarVisibility]);
  
  const handleEdgeTouchEnd = useCallback((e: TouchEvent) => {
    if (!swipeStartRef.current || swipeStartRef.current.zone !== 'edge') {
      swipeStartRef.current = null;
      return;
    }
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = touch.clientY - swipeStartRef.current.y;
    const edge = swipeStartRef.current.edge;
    
    // Check if swipe was in correct direction to show toolbar
    if (edge === 'top' && deltaY > SWIPE_THRESHOLD) {
      toggleToolbar('top', true);
    } else if (edge === 'bottom' && deltaY < -SWIPE_THRESHOLD) {
      toggleToolbar('bottom', true);
    } else if (edge === 'left' && deltaX > SWIPE_THRESHOLD) {
      toggleToolbar('left', true);
    } else if (edge === 'right' && deltaX < -SWIPE_THRESHOLD) {
      toggleToolbar('right', true);
    }
    
    swipeStartRef.current = null;
  }, [toggleToolbar]);
  
  // Add edge swipe detection listeners
  useEffect(() => {
    if (!visible) return;
    
    document.addEventListener('touchstart', handleEdgeTouchStart, { passive: true });
    document.addEventListener('touchend', handleEdgeTouchEnd, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', handleEdgeTouchStart);
      document.removeEventListener('touchend', handleEdgeTouchEnd);
    };
  }, [visible, handleEdgeTouchStart, handleEdgeTouchEnd]);

  // ============================================================================
  // LAYOUT PERSISTENCE
  // ============================================================================

  useEffect(() => {
    const versionKey = 'map_fab_layout_version';
    const savedVersion = localStorage.getItem(versionKey);

    // Reset layout if version changed or doesn't exist
    if (!savedVersion || parseInt(savedVersion) < LAYOUT_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(versionKey, String(LAYOUT_VERSION));
      setLayout(DEFAULT_LAYOUT);
      return;
    }

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as ZoneLayout;
        // Ensure all zones exist
        if (!parsed.left) parsed.left = [];
        if (!parsed.right) parsed.right = [];
        if (!parsed.top) parsed.top = [];
        if (!parsed.bottom) parsed.bottom = [];
        if (!parsed.archived) parsed.archived = [];
        setLayout(parsed);
      } catch (e) {
        console.error('Failed to parse map FAB layout:', e);
        setLayout(DEFAULT_LAYOUT);
      }
    }
  }, []);

  const saveLayout = useCallback((newLayout: ZoneLayout) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newLayout));
    setLayout(newLayout);
  }, []);

  // Reset layout to default
  const resetLayout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.setItem('map_fab_layout_version', String(LAYOUT_VERSION));
    setLayout(DEFAULT_LAYOUT);
    telegram.hapticFeedback('success');
    feedbackService.playSound('success');
    setShowResetConfirm(false);
  }, []);

  // Restore a FAB from trash
  const restoreFab = useCallback((fabId: string) => {
    const newLayout = { ...layout };
    
    // Remove from archived
    newLayout.archived = layout.archived.filter(id => id !== fabId);
    
    // Add to bottom zone by default
    if (!newLayout.bottom.includes(fabId)) {
      newLayout.bottom = [...newLayout.bottom, fabId];
    }
    
    saveLayout(newLayout);
    telegram.hapticFeedback('success');
    feedbackService.playSound('success');
    setTrashMenuAnchor(null);
  }, [layout, saveLayout]);

  // Get FAB label for menu display
  const getFabLabel = (fabId: string): string => {
    const labels: Record<string, string> = {
      home: 'Home',
      search: 'Search',
      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
      layers: 'Map Layers',
      facilityLayer: 'Facility Filter',
      truckStops: 'Truck Stops',
      heavyHaul: 'Heavy Haul Mode',
      myLocation: 'My Location',
      weather: 'Weather',
      wind: 'Wind',
      airQuality: 'Air Quality',
      pollen: 'Pollen',
      services: 'Nearby Services',
      elevation: 'Elevation Profile',
      settings: 'Settings',
      routesToggle: 'Routes',
      landmark: 'Landmarks',
      reset: 'Reset Layout',
    };
  return labels[fabId] || fabId;
};

// ============================================================================
// DRAG AND DROP
// ============================================================================

  const handleLongPressStart = useCallback((fabId: string, e: React.TouchEvent | React.MouseEvent) => {
    const pos = 'touches' in e ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };
    dragStartPos.current = pos;

    // Normal FABs use 500ms long-press for drag-reordering
    longPressTimer.current = setTimeout(() => {
      setIsDragMode(true);
      setDraggedFab(fabId);
      setDragPosition(pos);
      telegram.hapticFeedback('heavy');
      feedbackService.playSound('select');
    }, LONG_PRESS_DURATION);
  }, [onRequestLocation]);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // Persist even spacing preference
  useEffect(() => {
    localStorage.setItem('map_fab_even_spacing', String(evenTopBottomSpacing));
  }, [evenTopBottomSpacing]);

  // Global drag move handler
  const handleGlobalDragMove = useCallback((e: TouchEvent | MouseEvent) => {
    if (!draggedFab || !isDragMode) return;

    e.preventDefault();

    const pos = 'touches' in e ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };
    setDragPosition(pos);

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    // Check if dragging over the trash FAB (but not if dragging the trash itself)
    if (draggedFab !== 'trash') {
      const trashEl = fabRefs.current.get('trash');
      if (trashEl) {
        const trashRect = trashEl.getBoundingClientRect();
        const overTrash = 
          pos.x >= trashRect.left - 20 && 
          pos.x <= trashRect.right + 20 && 
          pos.y >= trashRect.top - 20 && 
          pos.y <= trashRect.bottom + 20;
        
        if (overTrash !== isOverTrash) {
          setIsOverTrash(overTrash);
          if (overTrash) {
            telegram.hapticFeedback('warning');
          }
        }
      }
    }

    // Determine which zone we're over
    let zone: MapFabZone | null = null;
    if (pos.y < 90) {
      zone = 'top';
    } else if (pos.y > windowHeight - 110) {
      zone = 'bottom';
    } else if (pos.x < 100) {
      zone = 'left';
    } else if (pos.x > windowWidth - 100) {
      zone = 'right';
    }

    if (zone !== dragOverZone) {
      setDragOverZone(zone);
      setDragOverIndex(null);
      if (zone) {
        telegram.hapticFeedback('light');
      }
    }

    // Find insert position within zone
    if (zone) {
      const zoneFabs = layout[zone];
      let foundIndex: number | null = null;

      for (let i = 0; i < zoneFabs.length; i++) {
        const fabId = zoneFabs[i];
        if (fabId === draggedFab) continue;

        const el = fabRefs.current.get(fabId);
        if (el) {
          const rect = el.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;

          if (zone === 'right' || zone === 'left') {
            if (pos.y < centerY) {
              foundIndex = i;
              break;
            }
          } else {
            if (pos.x < centerX) {
              foundIndex = i;
              break;
            }
          }
        }
      }

      if (foundIndex === null) {
        foundIndex = zoneFabs.filter(id => id !== draggedFab).length;
      }

      if (foundIndex !== dragOverIndex) {
        setDragOverIndex(foundIndex);
        telegram.hapticFeedback('light');
      }
    }
  }, [draggedFab, isDragMode, dragOverZone, dragOverIndex, layout, isOverTrash]);

  // Global drag end handler
  const handleGlobalDragEnd = useCallback(() => {
    handleLongPressEnd();

    if (draggedFab) {
      const currentZone = Object.entries(layout).find(([zone, ids]) => zone !== 'archived' && ids.includes(draggedFab))?.[0] as MapFabZone;

      if (currentZone) {
        const newLayout = { ...layout };

        // Check if dropped on trash - archive the FAB
        if (isOverTrash && draggedFab !== 'trash') {
          // Remove from current zone
          newLayout[currentZone] = layout[currentZone].filter(id => id !== draggedFab);
          // Add to archived
          newLayout.archived = [...(layout.archived || []), draggedFab];
          
          saveLayout(newLayout);
          telegram.hapticFeedback('success');
          feedbackService.playSound('success');
        } else if (dragOverZone !== null) {
          // Normal zone-to-zone move
          // Remove from current zone
          newLayout[currentZone] = layout[currentZone].filter(id => id !== draggedFab);

          // Insert at specific position in target zone
          const insertIndex = dragOverIndex !== null ? dragOverIndex : newLayout[dragOverZone].length;
          newLayout[dragOverZone] = [
            ...newLayout[dragOverZone].slice(0, insertIndex),
            draggedFab,
            ...newLayout[dragOverZone].slice(insertIndex)
          ];

          saveLayout(newLayout);
          telegram.hapticFeedback('success');
          feedbackService.playSound('success');
        }
      }
    }

    setDraggedFab(null);
    setDragOverZone(null);
    setDragOverIndex(null);
    setDragPosition(null);
    setIsDragMode(false);
    setIsOverTrash(false);
    dragStartPos.current = null;
  }, [draggedFab, dragOverZone, dragOverIndex, layout, saveLayout, handleLongPressEnd, isOverTrash]);

  // Attach global event listeners when dragging
  useEffect(() => {
    if (isDragMode && draggedFab) {
      document.addEventListener('touchmove', handleGlobalDragMove, { passive: false });
      document.addEventListener('mousemove', handleGlobalDragMove);
      document.addEventListener('touchend', handleGlobalDragEnd);
      document.addEventListener('mouseup', handleGlobalDragEnd);

      return () => {
        document.removeEventListener('touchmove', handleGlobalDragMove);
        document.removeEventListener('mousemove', handleGlobalDragMove);
        document.removeEventListener('touchend', handleGlobalDragEnd);
        document.removeEventListener('mouseup', handleGlobalDragEnd);
      };
    }
  }, [isDragMode, draggedFab, handleGlobalDragMove, handleGlobalDragEnd]);

  // ============================================================================
  // TRON LIGHT CYCLE ENTRANCE ANIMATION
  // ============================================================================

  useEffect(() => {
    if (visible && !fabsEntered) {
      // Stagger the entrance animation
      const timer = setTimeout(() => {
        setFabsEntered(true);
      }, 100);
      return () => clearTimeout(timer);
    }
    // Reset when hidden
    if (!visible && fabsEntered) {
      setFabsEntered(false);
    }
  }, [visible, fabsEntered]);

  // ============================================================================
  // FAB RENDERER
  // ============================================================================

  const renderFab = (fabId: string, zone: MapFabZone, index: number) => {
    const isBeingDragged = draggedFab === fabId;
    const isDropTarget = dragOverZone === zone && dragOverIndex === index && draggedFab !== fabId;
    const isLightMode = !darkMode;
    const isDirectionalFab = DIRECTIONAL_FAB_IDS.has(fabId);
    const currentFabSize = isDirectionalFab ? LARGE_FAB_SIZE : FAB_SIZE;

    // Tron Light Cycle entrance animation - FABs slide in from their zone edge
    const entranceDelay = index * 50; // Stagger each FAB by 50ms
    const getEntranceTransform = () => {
      if (fabsEntered) return 'translate(0, 0)';
      switch (zone) {
        case 'top': return 'translate(0, -100px)';
        case 'bottom': return 'translate(0, 100px)';
        case 'left': return 'translate(-100px, 0)';
        case 'right': return 'translate(100px, 0)';
      }
    };

    const fabSx = {
      width: currentFabSize,
      height: currentFabSize,
      opacity: isBeingDragged ? 0.3 : 1,
      transform: isBeingDragged ? 'scale(0.8)' : 'scale(1)',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: isDragMode ? 'grab' : 'pointer',
      backgroundColor: isLightMode ? '#ffffff' : undefined,
    };

    const wrapperSx = {
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      // Tron Light Cycle entrance animation
      transform: getEntranceTransform(),
      opacity: fabsEntered ? 1 : 0,
      transition: `transform 400ms cubic-bezier(0.22, 1, 0.36, 1) ${entranceDelay}ms, opacity 300ms ease ${entranceDelay}ms`,
      // Neon glow trail effect during entrance
      ...((!fabsEntered) && {
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: -4,
          borderRadius: '50%',
          background: zone === 'top' || zone === 'bottom' 
            ? `linear-gradient(${zone === 'top' ? '180deg' : '0deg'}, ${cyber.neonCyan}60, transparent 80%)`
            : `linear-gradient(${zone === 'left' ? '90deg' : '270deg'}, ${cyber.neonCyan}60, transparent 80%)`,
          filter: `blur(8px)`,
          opacity: 0.8,
          transition: 'opacity 200ms ease',
        },
      }),
    };

    // Drop indicator
    const dropIndicatorStyle = isDropTarget ? {
      '&::before': {
        content: '""',
        position: 'absolute',
        ...((zone === 'right' || zone === 'left')
          ? { top: -8, left: '50%', transform: 'translateX(-50%)', width: 40, height: 4 }
          : { left: -8, top: '50%', transform: 'translateY(-50%)', width: 4, height: 40 }
        ),
        background: cyber.neonCyan,
        borderRadius: 2,
        boxShadow: `0 0 10px ${cyber.neonCyan}`,
        animation: 'pulse 0.5s ease-in-out infinite',
      }
    } : {};

    // Pass-through components (landmarkFab, routeCreationFab)
    if (fabId === 'landmarkComponent' && landmarkFab) {
      return (
        <Box
          key={fabId}
          ref={(el: HTMLElement | null) => { if (el) fabRefs.current.set(fabId, el); }}
          sx={{ ...wrapperSx, ...dropIndicatorStyle }}
        >
          <Box
            sx={fabSx}
            onTouchStart={(e) => handleLongPressStart(fabId, e)}
            onTouchEnd={handleLongPressEnd}
            onMouseDown={(e) => handleLongPressStart(fabId, e)}
            onMouseUp={handleLongPressEnd}
            onMouseLeave={handleLongPressEnd}
          >
            {landmarkFab}
          </Box>
        </Box>
      );
    }

    if (fabId === 'routeCreation' && routeCreationFab) {
      return (
        <Box
          key={fabId}
          ref={(el: HTMLElement | null) => { if (el) fabRefs.current.set(fabId, el); }}
          sx={{ ...wrapperSx, ...dropIndicatorStyle }}
        >
          <Box
            sx={fabSx}
            onTouchStart={(e) => handleLongPressStart(fabId, e)}
            onTouchEnd={handleLongPressEnd}
            onMouseDown={(e) => handleLongPressStart(fabId, e)}
            onMouseUp={handleLongPressEnd}
            onMouseLeave={handleLongPressEnd}
          >
            {routeCreationFab}
          </Box>
        </Box>
      );
    }

    if (fabId === 'ai' && aiSpeedDial) {
      return (
        <Box
          key={fabId}
          ref={(el: HTMLElement | null) => { if (el) fabRefs.current.set(fabId, el); }}
          sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        >
          {aiSpeedDial}
        </Box>
      );
    }

    // Spacer - invisible placeholder to create gaps between FABs
    // Spacer IDs are like 'spacer_1', 'spacer_2', etc.
    if (fabId.startsWith('spacer_')) {
      return (
        <Box
          key={fabId}
          ref={(el: HTMLElement | null) => { if (el) fabRefs.current.set(fabId, el); }}
          sx={{ 
            ...wrapperSx, 
            ...dropIndicatorStyle,
          }}
        >
          <Box
            sx={{
              width: FAB_SIZE / 2,
              height: FAB_SIZE / 2,
              opacity: isDragMode ? 0.5 : 0.15,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: isDragMode ? `2px dashed ${cyber.neonPurple}` : `1px dashed ${cyber.textMuted}40`,
              backgroundColor: isDragMode ? `${cyber.neonPurple}15` : 'transparent',
              cursor: isDragMode ? 'grab' : 'default',
              transition: 'all 0.2s ease',
            }}
            onTouchStart={(e) => handleLongPressStart(fabId, e)}
            onTouchEnd={handleLongPressEnd}
            onMouseDown={(e) => handleLongPressStart(fabId, e)}
            onMouseUp={handleLongPressEnd}
            onMouseLeave={handleLongPressEnd}
          >
            {isDragMode && (
              <Typography sx={{ fontSize: 10, color: cyber.textMuted }}>···</Typography>
            )}
          </Box>
        </Box>
      );
    }

    // Get FAB config based on ID
    const getFabConfig = (): { icon: ReactNode; color: string; bgColor?: string; transparentBg?: boolean; onClick: (e?: React.MouseEvent<HTMLElement>) => void; label: string; badge?: number } | null => {
      switch (fabId) {
        case 'home':
          return {
            icon: <CloseIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonRed }} />,
            color: cyber.neonRed,
            bgColor: getFabBackground(cyber.neonRed, 'soft'),
            onClick: onClose,
            label: 'Close Map',
          };
        case 'voice': {
          const listening = voiceStatus === 'listening';
          const processing = voiceStatus === 'processing';
          const success = voiceStatus === 'success';
          const error = voiceStatus === 'error';
          const voiceColor = listening
            ? cyber.neonGreen
            : processing
              ? cyber.neonCyan
              : success
                ? cyber.neonGreen
                : error
                  ? cyber.neonRed
                  : cyber.neonBlue;
          const voiceLabel = listening
            ? 'Listening…'
            : processing
              ? 'Processing Voice'
              : success
                ? 'Voice Ready'
                : error
                  ? 'Voice Error'
                  : 'Voice Search (iOS)';
          const voiceIntensity: FabIntensity = listening || processing || success || error ? 'strong' : 'medium';
          return {
            icon: (
              <RecordVoiceOverIcon
                sx={{
                  fontSize: ICON_SIZE,
                  color: voiceColor,
                  animation: listening ? 'pulse 1.2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.12)' },
                    '100%': { transform: 'scale(1)' },
                  },
                }}
              />
            ),
            color: voiceColor,
            bgColor: getFabBackground(voiceColor, voiceIntensity),
            onClick: () => onVoiceClick?.(),
            label: voiceLabel,
          };
        }
        // case 'manualSearch':
        //   return {
        //     icon: <SearchIcon sx={{ fontSize: ICON_SIZE, color: '#e2e8f0' }} />,
        //     color: '#e2e8f0',
        //     bgColor: darkMode ? undefined : 'linear-gradient(135deg, rgba(226, 232, 240, 0.15) 0%, rgba(226, 232, 240, 0.3) 100%)',
        //     onClick: () => (onManualSearchClick ? onManualSearchClick() : onSearch()),
        //     label: 'Manual Search',
        //   };
        case 'search':
          return {
            icon: <SearchIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonCyan }} />,
            color: cyber.neonCyan,
            bgColor: getFabBackground(cyber.neonCyan, 'soft'),
            onClick: onSearch,
            label: 'Search Facilities',
          };
        case 'zoomIn':
          return {
            icon: <AddIcon sx={{ fontSize: LARGE_ICON_SIZE - 4, color: cyber.neonCyan }} />,
            color: cyber.neonCyan,
            bgColor: getFabBackground(cyber.neonCyan, 'soft'),
            onClick: onZoomIn,
            label: 'Zoom In',
          };
        case 'zoomOut':
          return {
            icon: <RemoveIcon sx={{ fontSize: LARGE_ICON_SIZE - 4, color: cyber.neonCyan }} />,
            color: cyber.neonCyan,
            bgColor: getFabBackground(cyber.neonCyan, 'soft'),
            onClick: onZoomOut,
            label: 'Zoom Out',
          };
        case 'layers':
          return {
            icon: <LayersIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonPurple }} />,
            color: cyber.neonPurple,
            bgColor: getFabBackground(cyber.neonPurple, 'soft'),
            onClick: onCycleMapStyle,
            label: `Map Style: ${mapStyle}`,
          };
        case 'myLocation':
          return {
            icon: <MyLocationIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonCyan }} />,
            color: cyber.neonCyan,
            bgColor: getFabBackground(cyber.neonCyan, 'soft'),
            onClick: onMyLocation,
            label: 'My Location',
          };
        case 'favorites':
          return {
            icon: <StarIcon sx={{ fontSize: ICON_SIZE, color: '#fbbf24' }} />,
            color: '#fbbf24',
            bgColor: getFabBackground('#fbbf24', 'soft'),
            onClick: onClose,
            label: 'Back to Favorites List',
          };
        case 'routePhotos':
          return {
            icon: (
              <Badge
                badgeContent={routePhotosCount}
                max={99}
                sx={makeBadgeSx(cyber.neonPurple)}
              >
                <PhotoLibraryIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonPurple }} />
              </Badge>
            ),
            color: cyber.neonPurple,
            bgColor: getFabBackground(cyber.neonPurple, 'soft'),
            onClick: () => onOpenRoutePhotos?.(),
            label: 'Route Photos',
          };
        case 'communityFacilities':
          return {
            icon: (
              <Badge
                badgeContent={communityFacilitiesCount}
                max={999}
                sx={makeBadgeSx(cyber.neonCyan)}
              >
                <GroupsIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonCyan }} />
              </Badge>
            ),
            color: cyber.neonCyan,
            bgColor: getFabBackground(cyber.neonCyan, 'soft'),
            onClick: () => {
              telegram.hapticFeedback('light');
              onToggleCommunityFacilities?.();
            },
            label: `Community Facilities (${communityFacilitiesCount})`,
          };
        case 'addFacility':
          return {
            icon: <AddLocationAltIcon sx={{ fontSize: ICON_SIZE, color: '#4ade80' }} />,
            color: '#4ade80',
            bgColor: getFabBackground('#4ade80', 'soft'),
            onClick: () => {
              telegram.hapticFeedback('light');
              onToggleAddFacility?.();
            },
            label: 'Add Facility',
          };
        case 'gpsToggle': {
          const gpsStatusColor = locationStatus === 'active'
            ? '#10b981'
            : locationStatus === 'pending'
              ? '#f59e0b'
              : locationStatus === 'denied'
                ? '#ef4444'
                : '#6b7280';

          const gpsMainIcon = personalLocationEnabled && crewSharingEnabled
            ? <GpsFixedIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonGreen }} />
            : personalLocationEnabled
              ? <MyLocationIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonCyan }} />
              : <GpsOffIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonRed }} />;

          return {
            icon: gpsMainIcon,
            color: gpsStatusColor,
            bgColor: getFabBackground(gpsStatusColor, 'medium'),
            onClick: () => setShowGpsMenu((prev) => !prev),
            label: personalLocationEnabled
              ? crewSharingEnabled
                ? 'Location & Crew ON'
                : 'Location ON'
              : 'Location Controls',
          };
        }
        case 'ai':
          return {
            icon: (
              <Box
                component="span"
                sx={{
                  fontSize: ICON_SIZE,
                  fontWeight: 900,
                  color: cyber.neonCyan,
                  fontFamily: 'monospace',
                  lineHeight: 1,
                  background: 'transparent',
                }}
              >
                AI
              </Box>
            ),
            color: cyber.neonCyan,
            transparentBg: true,
            onClick: () => {},
            label: 'Grok AI Assistant',
          };
        case 'weather':
          const weatherColor = showWeather ? cyber.neonBlue : cyber.textMuted;
          return {
            icon: showWeather 
              ? <CloudIcon sx={{ fontSize: ICON_SIZE, color: weatherColor }} />
              : <CloudOffIcon sx={{ fontSize: ICON_SIZE, color: cyber.textMuted }} />,
            color: weatherColor,
            bgColor: getFabBackground(weatherColor, showWeather ? 'medium' : 'soft'),
            onClick: onToggleWeather || (() => {}),
            label: showWeather ? 'Weather On' : 'Weather Off',
          };
        case 'temperature':
          // Temperature FAB with severity-based coloring
          // Use "feels like" temp for safety-focused coloring when available
          const effectiveTemp = feelsLike !== null ? feelsLike : temperature;
          const tempColor = effectiveTemp !== null
            ? effectiveTemp >= 110
              ? cyber.neonRed       // 🔴 Extreme heat - life threatening
              : effectiveTemp >= 100
                ? cyber.neonOrange  // 🟠 Dangerous heat - limit exposure
                : effectiveTemp >= 90
                  ? '#facc15'       // 🟡 Hot - stay hydrated
                  : effectiveTemp <= 32
                    ? '#60a5fa'     // 🔵 Freezing - ice hazards
                    : effectiveTemp <= 45
                      ? '#22d3ee'   // 🩵 Cold - dress warm
                      : cyber.neonGreen // 🟢 Comfortable
            : cyber.textMuted;
          return {
            icon: (
              <Badge
                badgeContent={temperature !== null ? `${Math.round(temperature)}°` : null}
                sx={makeBadgeSx(tempColor)}
              >
                <ThermostatIcon sx={{ fontSize: ICON_SIZE, color: tempColor }} />
              </Badge>
            ),
            color: tempColor,
            onClick: () => {}, // Temperature is display-only
            label: temperature !== null 
              ? `${Math.round(temperature)}°F${feelsLike !== null && Math.abs(feelsLike - temperature) >= 3 ? ` (Feels ${Math.round(feelsLike)}°)` : ''} - ${
                  effectiveTemp !== null 
                    ? effectiveTemp >= 110 ? '🔴 EXTREME HEAT!'
                      : effectiveTemp >= 100 ? '🟠 Dangerous Heat'
                      : effectiveTemp >= 90 ? '🟡 Hot - Hydrate!'
                      : effectiveTemp <= 32 ? '🔵 Freezing - Ice!'
                      : effectiveTemp <= 45 ? '🩵 Cold'
                      : '🟢 Comfortable'
                    : ''
                }`.trim()
              : 'Temperature Loading...',
          };
        case 'uvIndex':
          // UV Index with EPA standard color coding
          // 0-2 Green, 3-5 Yellow, 6-7 Orange, 8-10 Red, 11+ Purple
          const uvSeverityColor = uvIndex !== null
            ? uvIndex >= 11
              ? '#a855f7'         // 🟣 Extreme - avoid sun
              : uvIndex >= 8
                ? cyber.neonRed   // 🔴 Very High - minimize exposure
                : uvIndex >= 6
                  ? cyber.neonOrange // 🟠 High - seek shade
                  : uvIndex >= 3
                    ? '#facc15'   // 🟡 Moderate - wear sunscreen
                    : cyber.neonGreen // 🟢 Low - safe
            : cyber.textMuted;
          return {
            icon: (
              <Badge
                badgeContent={uvIndex !== null ? Math.round(uvIndex) : null}
                sx={makeBadgeSx(uvSeverityColor)}
              >
                <WbSunnyIcon sx={{ fontSize: ICON_SIZE, color: uvSeverityColor }} />
              </Badge>
            ),
            color: uvSeverityColor,
            onClick: () => {}, // UV is display-only
            label: uvIndex !== null 
              ? `UV Index: ${Math.round(uvIndex)} - ${
                  uvIndex >= 11 ? '🟣 EXTREME!'
                  : uvIndex >= 8 ? '🔴 Very High'
                  : uvIndex >= 6 ? '🟠 High - Shade'
                  : uvIndex >= 3 ? '🟡 Moderate'
                  : '🟢 Low'
                }`
              : 'UV Index Loading...',
          };
        case 'wind':
          // Wind speed with severity coloring for work safety
          // Important for crane ops, high-profile vehicles, tarping loads
          const windColor = windSpeed !== null 
            ? windSpeed >= 35
              ? cyber.neonRed       // 🔴 Dangerous - stop crane ops
              : windSpeed >= 25 
                ? cyber.neonOrange  // 🟠 High - secure loose items
                : windSpeed >= 15 
                  ? '#facc15'       // 🟡 Breezy - caution with tarps
                  : cyber.neonGreen // 🟢 Calm
            : (showWind ? cyber.neonGreen : cyber.textMuted);
          return {
            icon: (
              <Badge
                badgeContent={windSpeed !== null ? Math.round(windSpeed) : null}
                sx={makeBadgeSx(windColor)}
              >
                <AirIcon sx={{ fontSize: ICON_SIZE, color: windColor }} />
              </Badge>
            ),
            color: windColor,
            onClick: onToggleWind || (() => {}),
            label: windSpeed !== null 
              ? `Wind: ${Math.round(windSpeed)} mph - ${
                  windSpeed >= 35 ? '🔴 DANGEROUS!'
                  : windSpeed >= 25 ? '🟠 High Wind'
                  : windSpeed >= 15 ? '🟡 Breezy'
                  : '🟢 Calm'
                }`
              : (showWind ? 'Wind Visible' : 'Show Wind'),
          };
        case 'rain':
          // Rain/Precipitation with flash flood awareness
          // Desert arroyos (dry washes) can flash flood in minutes!
          const rainSeverityColor = precipProbability !== null
            ? precipProbability >= 70
              ? cyber.neonRed       // 🔴 Flash flood risk!
              : precipProbability >= 50
                ? cyber.neonOrange  // 🟠 Heavy rain likely
                : precipProbability >= 30
                  ? '#facc15'       // 🟡 Rain possible
                  : cyber.neonGreen // 🟢 Clear/light chance
            : cyber.textMuted;
          const isFlashFloodRisk = precipProbability !== null && precipProbability >= 70;
          const isThunderstorm = thunderstormProbability !== null && thunderstormProbability >= 50;
          return {
            icon: (
              <Badge
                badgeContent={precipProbability !== null ? `${precipProbability}%` : null}
                sx={{
                  ...makeBadgeSx(rainSeverityColor),
                  '& .MuiBadge-badge': {
                    ...makeBadgeSx(rainSeverityColor)['& .MuiBadge-badge'],
                    ...(isFlashFloodRisk && {
                      animation: 'pulse-danger 1.5s ease-in-out infinite',
                    }),
                  },
                  '@keyframes pulse-danger': {
                    '0%, 100%': { 
                      boxShadow: `0 0 8px ${cyber.neonRed}40`,
                      transform: 'scale(1)',
                    },
                    '50%': { 
                      boxShadow: `0 0 20px ${cyber.neonRed}80, 0 0 30px ${cyber.neonRed}40`,
                      transform: 'scale(1.1)',
                    },
                  },
                }}
              >
                <WaterDropIcon 
                  sx={{ 
                    fontSize: ICON_SIZE, 
                    color: rainSeverityColor,
                    // Icon also pulses on flash flood risk
                    ...(isFlashFloodRisk && {
                      animation: 'icon-pulse 1.5s ease-in-out infinite',
                    }),
                    '@keyframes icon-pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.6 },
                    },
                  }} 
                />
              </Badge>
            ),
            color: rainSeverityColor,
            bgColor: isFlashFloodRisk 
              ? getFabBackground(cyber.neonRed, 'strong') 
              : isThunderstorm 
                ? getFabBackground(cyber.neonOrange, 'medium')
                : getFabBackground(rainSeverityColor, 'soft'),
            onClick: () => {}, // Rain is display-only - warnings are automatic
            label: precipProbability !== null 
              ? isFlashFloodRisk 
                ? `🔴 FLOOD RISK: ${precipProbability}% - Avoid low areas!`
                : isThunderstorm
                  ? `🟠 Storm: ${thunderstormProbability}% - Seek shelter!`
                  : precipProbability >= 50 
                    ? `🟠 Heavy Rain: ${precipProbability}%`
                    : precipProbability >= 30
                      ? `🟡 Rain: ${precipProbability}%`
                      : `🟢 Clear: ${precipProbability}%`
              : 'Rain Data Loading...',
          };
        case 'airQuality':
          // AQI with EPA standard color coding
          // Important for respiratory health, especially near flares/refineries
          const aqiSeverityColor = aqiValue !== null
            ? aqiValue > 300
              ? '#7f1d1d'         // 🟤 Hazardous - stay inside!
              : aqiValue > 200
                ? '#a855f7'       // 🟣 Very Unhealthy - avoid all outdoor
                : aqiValue > 150
                  ? cyber.neonRed // 🔴 Unhealthy - limit outdoor
                  : aqiValue > 100
                    ? cyber.neonOrange // 🟠 Unhealthy for sensitive
                    : aqiValue > 50
                      ? '#facc15' // 🟡 Moderate
                      : cyber.neonGreen // 🟢 Good
            : cyber.textMuted;
          return {
            icon: (
              <Badge
                badgeContent={aqiValue}
                sx={makeBadgeSx(aqiSeverityColor)}
              >
                <MasksIcon sx={{ fontSize: ICON_SIZE, color: aqiSeverityColor }} />
              </Badge>
            ),
            color: aqiSeverityColor,
            onClick: onToggleAirQuality || (() => {}),
            label: aqiValue !== null 
              ? `AQI: ${aqiValue} - ${
                  aqiValue > 300 ? '🟤 HAZARDOUS!'
                  : aqiValue > 200 ? '🟣 Very Unhealthy'
                  : aqiValue > 150 ? '🔴 Unhealthy'
                  : aqiValue > 100 ? '🟠 Sensitive'
                  : aqiValue > 50 ? '🟡 Moderate'
                  : '🟢 Good'
                }`
              : 'Air Quality Loading...',
          };
        case 'pollen':
          // Pollen index with severity coloring
          // Scale is 0-5 (Very Low to Very High)
          const pollenSeverityColor = pollenIndex !== null
            ? pollenIndex >= 5
              ? '#a855f7'         // 🟣 Very High - stay inside if allergic
              : pollenIndex >= 4
                ? cyber.neonRed   // 🔴 High - take meds
                : pollenIndex >= 3
                  ? cyber.neonOrange // 🟠 Moderate - some may react
                  : pollenIndex >= 2
                    ? '#facc15'   // 🟡 Low - minimal impact
                    : cyber.neonGreen // 🟢 Very Low
            : (showPollen ? cyber.neonOrange : cyber.textMuted);
          return {
            icon: (
              <Badge
                badgeContent={pollenIndex}
                sx={makeBadgeSx(pollenSeverityColor)}
              >
                <LocalFloristIcon sx={{ fontSize: ICON_SIZE, color: pollenSeverityColor }} />
              </Badge>
            ),
            color: pollenSeverityColor,
            onClick: onTogglePollen || (() => {}),
            label: pollenIndex !== null 
              ? `Pollen: ${pollenIndex}/5 - ${
                  pollenIndex >= 5 ? '🟣 Very High!'
                  : pollenIndex >= 4 ? '🔴 High'
                  : pollenIndex >= 3 ? '🟠 Moderate'
                  : pollenIndex >= 2 ? '🟡 Low'
                  : '🟢 Very Low'
                }`
              : (showPollen ? 'Pollen On' : 'Pollen Off'),
          };
        case 'services':
          const servicesColor = showNearbyServices ? cyber.neonCyan : cyber.textMuted;
          return {
            icon: <StorefrontIcon sx={{ fontSize: ICON_SIZE, color: servicesColor }} />,
            color: servicesColor,
            bgColor: getFabBackground(servicesColor, showNearbyServices ? 'medium' : 'soft'),
            onClick: onToggleNearbyServices || (() => {}),
            label: 'Nearby Services',
          };
        case 'elevation':
          const elevationColor = showElevation ? cyber.neonGreen : (hasElevationPath ? cyber.textSecondary : cyber.textMuted);
          return {
            icon: <TerrainIcon sx={{ fontSize: ICON_SIZE, color: elevationColor }} />,
            color: elevationColor,
            bgColor: getFabBackground(elevationColor, showElevation ? 'medium' : 'soft'),
            onClick: onToggleElevation || (() => {}),
            label: showElevation ? 'Elevation On' : (hasElevationPath ? 'Show Elevation' : 'No Route'),
          };
        case 'settings':
          return {
            icon: (
              <SettingsIcon
                sx={{
                  fontSize: ICON_SIZE,
                  color: showSettingsMenu ? cyber.neonPurple : cyber.textSecondary,
                  transform: showSettingsMenu ? 'rotate(90deg)' : 'none',
                  transition: 'transform 0.25s ease, color 0.2s ease',
                }}
              />
            ),
            color: cyber.neonPurple,
            bgColor: getFabBackground(cyber.neonPurple, showSettingsMenu ? 'strong' : 'soft'),
            onClick: () => {
              telegram.hapticFeedback('light');
              onSettings();
              setShowSettingsMenu(prev => !prev);
            },
            label: showSettingsMenu ? 'Close Settings' : 'Settings',
          };
        // ========== ROAD HAZARD REPORTING ==========
        case 'roadHazard':
          return {
            icon: <WarningAmberIcon sx={{ fontSize: ICON_SIZE, color: '#f59e0b' }} />,
            color: '#f59e0b',
            bgColor: getFabBackground('#f59e0b', 'medium'),
            onClick: () => {
              telegram.hapticFeedback('medium');
              onReportHazard?.();
            },
            label: 'Report Road Hazard',
          };
        // ========== COP ALERT REPORTING ==========
        case 'copAlert':
          return {
            icon: <LocalPoliceIcon sx={{ fontSize: ICON_SIZE, color: '#3b82f6' }} />,
            color: '#3b82f6',
            bgColor: getFabBackground('#3b82f6', 'medium'),
            onClick: () => {
              telegram.hapticFeedback('medium');
              onReportCop?.();
            },
            label: 'Report Police',
          };
        // ========== SAFETY GUY - SAFETY INSPECTOR ALERT (PINK SHIELD) ==========
        case 'safetyGuy':
          return {
            icon: <LocalPoliceIcon sx={{ fontSize: ICON_SIZE, color: '#ec4899' }} />,
            color: '#ec4899',
            bgColor: getFabBackground('#ec4899', 'medium'),
            onClick: () => {
              telegram.hapticFeedback('medium');
              onReportSafetyGuy?.();
            },
            label: 'Report Safety Inspector',
          };
        case 'poop':
          return {
            icon: (
              <Box
                component="span"
                sx={{
                  fontSize: ICON_SIZE,
                  lineHeight: 1,
                  filter: 'drop-shadow(0 0 8px rgba(139,69,19,0.45))',
                }}
                aria-hidden
              >
                💩
              </Box>
            ),
            color: '#8B4513',
            bgColor: getFabBackground('#8B4513', 'medium'),
            onClick: () => {
              telegram.hapticFeedback('medium');
              onReportPoop?.();
            },
            label: poopPinCount > 0
              ? `💩 Restrooms nearby: ${poopPinCount}`
              : 'Drop Restroom Pin',
            badge: poopPinCount > 0 ? poopPinCount : undefined,
          };
        case 'taco':
          return {
            icon: (
              <Box
                component="span"
                sx={{
                  fontSize: ICON_SIZE,
                  lineHeight: 1,
                  filter: 'drop-shadow(0 0 8px rgba(202,138,4,0.45))',
                }}
                aria-hidden
              >
                🌮
              </Box>
            ),
            color: '#ca8a04',
            bgColor: getFabBackground('#ca8a04', 'medium'),
            onClick: () => {
              telegram.hapticFeedback('medium');
              onReportTaco?.();
            },
            label: tacoPinCount > 0
              ? `🌮 Taco trucks nearby: ${tacoPinCount}`
              : 'Drop Taco Truck Pin',
            badge: tacoPinCount > 0 ? tacoPinCount : undefined,
          };
        // ========== GHOST MODE - INCOGNITO LOCATION ==========
        case 'ghost': {
          const ghostColor = ghostModeEnabled ? '#a855f7' : cyber.textMuted;
          return {
            icon: <VisibilityOffIcon sx={{ 
              fontSize: ICON_SIZE, 
              color: ghostColor,
              animation: ghostModeEnabled ? 'ghost-pulse 2s ease-in-out infinite' : 'none',
              '@keyframes ghost-pulse': {
                '0%, 100%': { opacity: 0.7 },
                '50%': { opacity: 1 },
              },
            }} />,
            color: ghostColor,
            bgColor: getFabBackground(ghostColor, ghostModeEnabled ? 'strong' : 'soft'),
            onClick: () => {
              telegram.hapticFeedback('medium');
              onToggleGhostMode?.();
            },
            label: ghostModeEnabled ? '👻 Ghost Mode ON - Invisible' : 'Ghost Mode - Go Invisible',
          };
        }
        case 'routesToggle':
          return {
            icon: <RouteIcon sx={{ 
              fontSize: ICON_SIZE, 
              color: showRoutes ? cyber.neonCyan : cyber.neonOrange,
              animation: isLoadingRoutes ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }} />,
            color: showRoutes ? cyber.neonCyan : cyber.neonOrange,
            bgColor: getFabBackground(showRoutes ? cyber.neonCyan : cyber.neonOrange, showRoutes ? 'medium' : 'soft'),
            onClick: isLoadingRoutes ? () => {} : onToggleRoutes,
            label: isLoadingRoutes ? 'Loading Routes...' : (showRoutes ? `Hide Routes (${routeCount})` : 'Show Routes'),
            badge: showRoutes && routeCount > 0 ? routeCount : undefined,
          };
        case 'landmark':
          return {
            icon: <FlagIcon sx={{ fontSize: ICON_SIZE, color: isLandmarkMode ? cyber.neonCyan : '#22c55e' }} />,
            color: isLandmarkMode ? cyber.neonCyan : '#22c55e',
            onClick: onToggleLandmarkMode,
            label: isLandmarkMode ? 'Close Landmark Menu' : 'Build Landmark Route',
          };
        case 'facilityLayer':
          // Dynamic icon and color based on current facility layer - covers ALL facility type codes
          const getLayerIcon = () => {
            if (facilityLayerIcon) return facilityLayerIcon;
            switch (currentFacilityLayer) {
              // Favorites - personal saved favorites
              case 'favorites': return <StarIcon sx={{ fontSize: ICON_SIZE, color: '#fbbf24' }} />;
              // Main types
              case 'well': return <LocalGasStationIcon sx={{ fontSize: ICON_SIZE, color: '#f59e0b' }} />;
              case 'swd': return <WaterDropIcon sx={{ fontSize: ICON_SIZE, color: '#10b981' }} />;
              case 'rig': return <ConstructionIcon sx={{ fontSize: ICON_SIZE, color: '#fbbf24' }} />;
              // Tank & Storage
              case 'tb': return <BoltIcon sx={{ fontSize: ICON_SIZE, color: '#06b6d4' }} />;
              case 'gs': return <PropaneTankIcon sx={{ fontSize: ICON_SIZE, color: '#fbbf24' }} />;
              case 'bgt': return <LandscapeIcon sx={{ fontSize: ICON_SIZE, color: '#78716c' }} />;
              // Flare & Emissions
              case 'fs': return <WhatshotIcon sx={{ fontSize: ICON_SIZE, color: '#ef4444' }} />;
              // Compressor & Pumps
              case 'cs': return <PrecisionManufacturingIcon sx={{ fontSize: ICON_SIZE, color: '#60a5fa' }} />;
              case 'cp': return <AirIcon sx={{ fontSize: ICON_SIZE, color: '#64748b' }} />;
              // Plants & Processing
              case 'gp': return <FactoryIcon sx={{ fontSize: ICON_SIZE, color: '#a855f7' }} />;
              case 'rf': return <OilBarrelIcon sx={{ fontSize: ICON_SIZE, color: '#a855f7' }} />;
              case 'tp': return <ScienceIcon sx={{ fontSize: ICON_SIZE, color: '#f472b6' }} />;
              case 'inj': return <MedicationLiquidIcon sx={{ fontSize: ICON_SIZE, color: '#34d399' }} />;
              // Pipelines
              case 'plg': return <LinearScaleIcon sx={{ fontSize: ICON_SIZE, color: '#3b82f6' }} />;
              case 'plc': return <LinearScaleIcon sx={{ fontSize: ICON_SIZE, color: '#64748b' }} />;
              case 'plw': return <LinearScaleIcon sx={{ fontSize: ICON_SIZE, color: '#0ea5e9' }} />;
              // Gathering & Metering
              case 'ggs': return <HubIcon sx={{ fontSize: ICON_SIZE, color: '#3b82f6' }} />;
              case 'mgs': return <SpeedIcon sx={{ fontSize: ICON_SIZE, color: '#8b5cf6' }} />;
              case 'wh': return <GridViewIcon sx={{ fontSize: ICON_SIZE, color: '#f59e0b' }} />;
              // Brine Well
              case 'bw': return <OpacityIcon sx={{ fontSize: ICON_SIZE, color: '#0ea5e9' }} />;
              // Waste & Environmental
              case 'pit': return <LandscapeIcon sx={{ fontSize: ICON_SIZE, color: '#78716c' }} />;
              case 'wm': return <DeleteOutlineIcon sx={{ fontSize: ICON_SIZE, color: '#ef4444' }} />;
              case 'rfl': return <RecyclingIcon sx={{ fontSize: ICON_SIZE, color: '#22c55e' }} />;
              case 'rm': return <HealingIcon sx={{ fontSize: ICON_SIZE, color: '#eab308' }} />;
              case 'ab': return <CleaningServicesIcon sx={{ fontSize: ICON_SIZE, color: '#f97316' }} />;
              // Service & Regulatory
              case 'sc': return <EngineeringIcon sx={{ fontSize: ICON_SIZE, color: '#64748b' }} />;
              case 'ci': return <VerifiedIcon sx={{ fontSize: ICON_SIZE, color: '#34d399' }} />;
              // Generic facility
              case 'facility': return <BusinessIcon sx={{ fontSize: ICON_SIZE, color: '#a855f7' }} />;
              // All types (default)
              default: return <FilterListIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonOrange }} />;
            }
          };
          // Full color map for all facility types
          const layerColorMap: Record<string, string> = {
            all: cyber.neonOrange,
            favorites: '#fbbf24',
            well: '#f59e0b',
            swd: '#10b981',
            rig: '#fbbf24',
            tb: '#06b6d4',
            gs: '#fbbf24',
            bgt: '#78716c',
            fs: '#ef4444',
            cs: '#60a5fa',
            cp: '#64748b',
            gp: '#a855f7',
            rf: '#a855f7',
            tp: '#f472b6',
            inj: '#34d399',
            plg: '#3b82f6',
            plc: '#64748b',
            plw: '#0ea5e9',
            ggs: '#3b82f6',
            mgs: '#8b5cf6',
            wh: '#f59e0b',
            bw: '#0ea5e9',
            pit: '#78716c',
            wm: '#ef4444',
            rfl: '#22c55e',
            rm: '#eab308',
            ab: '#f97316',
            sc: '#64748b',
            ci: '#34d399',
            facility: '#a855f7',
          };
          // Human-readable labels for facility types
          const layerLabelMap: Record<string, string> = {
            all: 'All Types',
            favorites: 'My Favorites',
            well: 'Wells',
            swd: 'SWD',
            rig: 'Rigs',
            tb: 'Tank Battery',
            gs: 'Gas Storage',
            bgt: 'Below Grade Tank',
            fs: 'Flare Stack',
            cs: 'Compressor',
            cp: 'Crude Pump',
            gp: 'Gas Plant',
            rf: 'Refinery',
            tp: 'Treating Plant',
            inj: 'Injection',
            plg: 'Gas Pipeline',
            plc: 'Crude Pipeline',
            plw: 'Water Pipeline',
            ggs: 'Gas Gathering',
            mgs: 'Metering',
            wh: 'Well Head',
            bw: 'Brine Well',
            pit: 'Pit',
            wm: 'Waste Mgmt',
            rfl: 'Recycling',
            rm: 'Remediate',
            ab: 'Abatement',
            sc: 'Service Co',
            ci: 'Class I/II',
            facility: 'Facility',
          };
          return {
            icon: isLoadingFacilityLayer ? (
              <CircularProgress size={ICON_SIZE - 4} sx={{ color: layerColorMap[currentFacilityLayer] || cyber.neonOrange }} />
            ) : getLayerIcon(),
            color: layerColorMap[currentFacilityLayer] || cyber.neonOrange,
            onClick: onCycleFacilityLayer,
            label: isLoadingFacilityLayer 
              ? `Loading ${layerLabelMap[currentFacilityLayer] || currentFacilityLayer.toUpperCase()}...` 
              : `Filter: ${layerLabelMap[currentFacilityLayer] || currentFacilityLayer.toUpperCase()}`,
          };
        case 'truckStops':
          // Truck stops FAB - toggle for showing truck stops (Loves, Pilot, TA, Flying J)
          const truckColor = showTruckStops ? '#f59e0b' : cyber.textMuted; // Amber when active
          return {
            icon: isLoadingTruckStops ? (
              <CircularProgress size={ICON_SIZE - 4} sx={{ color: '#f59e0b' }} />
            ) : (
              <LocalShippingIcon sx={{ 
                fontSize: ICON_SIZE, 
                color: truckColor,
                filter: showTruckStops ? 'drop-shadow(0 0 6px #f59e0b)' : 'none',
              }} />
            ),
            color: truckColor,
            bgColor: getFabBackground(showTruckStops ? '#f59e0b' : truckColor, showTruckStops ? 'medium' : 'soft'),
            onClick: onToggleTruckStops || (() => {}),
            label: isLoadingTruckStops 
              ? 'Loading truck stops...'
              : showTruckStops 
                ? `Truck Stops ON (${truckStopCount} nearby) - Tap to hide`
                : 'Show Truck Stops (Loves, Pilot, TA, Flying J)',
            badge: showTruckStops && truckStopCount > 0 ? truckStopCount : undefined,
          };
        case 'heavyHaul':
          // Heavy Haul / Hazmat mode - routes avoid highways & tolls for oversized loads
          const heavyHaulColor = '#f97316'; // Orange for hazmat/heavy haul
          return {
            icon: (
              <WarningAmberIcon sx={{ 
                fontSize: ICON_SIZE, 
                color: heavyHaulMode ? heavyHaulColor : '#666666',
                filter: heavyHaulMode ? `drop-shadow(0 0 8px ${heavyHaulColor})` : 'none',
              }} />
            ),
            color: heavyHaulColor,
            bgColor: getFabBackground(heavyHaulColor, heavyHaulMode ? 'strong' : 'soft'),
            onClick: onToggleHeavyHaul || (() => {}),
            label: heavyHaulMode 
              ? '🚛 Heavy Haul Mode ON - Routes avoid highways & tolls. Tap to disable.'
              : '🚛 Enable Heavy Haul Mode for semis, oversized loads, and hazmat routing',
          };
        // COMMENTED OUT - OCD Activity Layer disabled
        // case 'ocdActivity':
        //   // OCD Activity Layer - spills, incidents, pits, frac activity from NM OCD
        //   const ocdColor = showOcdActivity ? '#ef4444' : '#f97316'; // Red when active, orange when off
        //   return {
        //     icon: isLoadingOcdActivity ? (
        //       <CircularProgress size={ICON_SIZE - 4} sx={{ color: ocdColor }} />
        //     ) : (
        //       <Badge
        //         badgeContent={showOcdActivity && ocdActivityCount > 0 ? ocdActivityCount : null}
        //         sx={{
        //           '& .MuiBadge-badge': {
        //             backgroundColor: `${ocdColor}30`,
        //             color: ocdColor,
        //             fontSize: '10px',
        //             fontWeight: 800,
        //             minWidth: '18px',
        //             height: '16px',
        //             borderRadius: '8px',
        //             border: `1px solid ${ocdColor}60`,
        //           },
        //         }}
        //       >
        //         <WarningAmberIcon sx={{ 
        //           fontSize: ICON_SIZE, 
        //           color: ocdColor,
        //           filter: showOcdActivity ? `drop-shadow(0 0 8px ${ocdColor})` : 'none',
        //           animation: showOcdActivity ? 'none' : 'none',
        //         }} />
        //       </Badge>
        //     ),
        //     color: ocdColor,
        //     bgColor: showOcdActivity 
        //       ? `linear-gradient(135deg, ${ocdColor}25 0%, ${ocdColor}45 100%)`
        //       : `linear-gradient(135deg, ${ocdColor}15 0%, ${ocdColor}25 100%)`,
        //     onClick: onToggleOcdActivity || (() => {}),
        //     label: isLoadingOcdActivity 
        //       ? 'Loading OCD Activity...'
        //       : showOcdActivity 
        //         ? `🏗️ OCD Activity ON (${ocdActivityCount} items) - Spills, Pits, Frac Jobs`
        //         : '🏗️ Show OCD Activity Layer - Incidents, Pits, Frac Activity',
        //   };
        case 'calichemRoutes':
          // Caliche Routes Layer - community landmark routes
          const routeColor = showCalichemRoutes ? '#22c55e' : '#f59e0b'; // Green when active, amber when off
          return {
            icon: isLoadingCalichemRoutes ? (
              <CircularProgress size={ICON_SIZE - 4} sx={{ color: routeColor }} />
            ) : (
              <Badge
                badgeContent={showCalichemRoutes && calichemRoutesCount > 0 ? calichemRoutesCount : null}
                sx={makeBadgeSx(routeColor)}
              >
                <RouteIcon sx={{ 
                  fontSize: ICON_SIZE, 
                  color: routeColor,
                  filter: showCalichemRoutes ? `drop-shadow(0 0 8px ${routeColor})` : 'none',
                }} />
              </Badge>
            ),
            color: routeColor,
            bgColor: getFabBackground(routeColor, showCalichemRoutes ? 'strong' : 'soft'),
            onClick: onToggleCalichemRoutes || (() => {}),
            label: isLoadingCalichemRoutes 
              ? 'Loading Caliche Routes...'
              : showCalichemRoutes 
                ? `🛤️ Caliche Routes ON (${calichemRoutesCount}) - Community dirt road routes`
                : '🛤️ Show Caliche Routes - Community dirt road directions',
          };
        case 'vehicle':
          // Vehicle selector FAB - shows current vehicle with MUI icon
          const vehicleColor = cyber.neonGreen;
          const getVehicleIcon = () => {
            switch (currentVehicle) {
              case 'tractor': return <AgricultureIcon sx={{ fontSize: ICON_SIZE, color: vehicleColor }} />;
              case 'semi': return <LocalShippingIcon sx={{ fontSize: ICON_SIZE, color: vehicleColor }} />;
              case 'welding': return <FireTruckIcon sx={{ fontSize: ICON_SIZE, color: vehicleColor }} />;
              case 'pickup': return <LocalShippingIcon sx={{ fontSize: ICON_SIZE - 4, color: vehicleColor }} />;
              case 'company': return <DirectionsCarIcon sx={{ fontSize: ICON_SIZE, color: '#ffffff' }} />;
              case 'vacuum': return <LocalShippingIcon sx={{ fontSize: ICON_SIZE, color: '#8B4513' }} />;
              case 'rig': return <OilBarrelIcon sx={{ fontSize: ICON_SIZE, color: vehicleColor }} />;
              case 'car': return <DirectionsCarIcon sx={{ fontSize: ICON_SIZE, color: vehicleColor }} />;
              case 'safety': return <HealthAndSafetyIcon sx={{ fontSize: ICON_SIZE, color: '#FF6B00' }} />;
              case 'water': return <LocalShippingIcon sx={{ fontSize: ICON_SIZE, color: '#00BFFF' }} />;
              case 'hotOiler': return <LocalShippingIcon sx={{ fontSize: ICON_SIZE, color: '#FF4500' }} />;
              case 'oilTruck': return <LocalShippingIcon sx={{ fontSize: ICON_SIZE, color: '#1a1a1a' }} />;
              case 'poop': return <LocalShippingIcon sx={{ fontSize: ICON_SIZE, color: '#8B4513' }} />;
              case 'monster': return <LocalShippingIcon sx={{ fontSize: ICON_SIZE, color: '#FF1493' }} />;
              case 'electric': return <ElectricCarIcon sx={{ fontSize: ICON_SIZE, color: '#00D4AA' }} />;
              case 'vapor': return <AirIcon sx={{ fontSize: ICON_SIZE, color: '#9370DB' }} />;
              case 'crane': return <PrecisionManufacturingIcon sx={{ fontSize: ICON_SIZE, color: '#FFD700' }} />;
              case 'sandTank': return <LocalShippingIcon sx={{ fontSize: ICON_SIZE, color: '#C2B280' }} />;
              case 'raceCar': return <SportsScoreIcon sx={{ fontSize: ICON_SIZE, color: '#FF0000' }} />;
              case 'motorcycle': return <TwoWheelerIcon sx={{ fontSize: ICON_SIZE, color: '#FF6600' }} />;
              case 'eagle': return <FlightIcon sx={{ fontSize: ICON_SIZE, color: '#FFD700' }} />;
              case 'fracking': return <OilBarrelIcon sx={{ fontSize: ICON_SIZE, color: '#FF4500' }} />;
              case 'drilling': return <ConstructionIcon sx={{ fontSize: ICON_SIZE, color: '#8B4513' }} />;
              case 'roadrunner': return <DirectionsRunIcon sx={{ fontSize: ICON_SIZE, color: '#00CED1' }} />;
              default: return <MyLocationIcon sx={{ fontSize: ICON_SIZE, color: vehicleColor }} />;
            }
          };
          const vehicleNames: Record<string, string> = {
            dot: 'Green Dot',
            tractor: 'Tractor',
            semi: 'Semi Truck',
            welding: 'Welding Truck',
            pickup: 'Pickup',
            company: 'Company Man',
            vacuum: 'Vacuum Truck',
            rig: 'Drilling Rig',
            car: 'Car',
            safety: 'Safety Truck',
            water: 'Water Truck',
            hotOiler: 'Hot Oiler',
            oilTruck: 'Oil Truck',
            poop: 'Vacuum Truck',
            monster: 'Monster Truck',
            electric: 'Electric Car',
            vapor: 'Vapor Recovery',
            crane: 'Crane',
            sandTank: 'Sand Tank',
            raceCar: 'Race Car',
            motorcycle: 'Motorcycle',
            eagle: 'Eagle',
            fracking: 'Fracking Unit',
            drilling: 'Drilling Unit',
            roadrunner: 'Road Runner',
          };
          return {
            icon: getVehicleIcon(),
            color: vehicleColor,
            bgColor: getFabBackground(vehicleColor, 'soft'),
            onClick: onOpenVehicleMenu || (() => {}),
            label: `Vehicle: ${vehicleNames[currentVehicle] || 'Green Dot'} - Tap to change`,
          };
        case 'trash':
          const archivedCount = layout.archived?.length || 0;
          const trashActive = isOverTrash && draggedFab && draggedFab !== 'trash';
          const trashColor = trashActive ? cyber.neonRed : (archivedCount > 0 ? cyber.neonRed : cyber.textMuted);
          const trashIntensity: FabIntensity = trashActive ? 'strong' : archivedCount > 0 ? 'medium' : 'soft';
          return {
            icon: <DeleteIcon sx={{ 
              fontSize: ICON_SIZE, 
              color: trashColor,
              transform: trashActive ? 'scale(1.3)' : 'scale(1)',
              transition: 'transform 0.2s ease',
            }} />,
            color: trashColor,
            bgColor: getFabBackground(trashColor, trashIntensity),
            onClick: (e?: React.MouseEvent<HTMLElement>) => {
              if (e) setTrashMenuAnchor(e.currentTarget);
            },
            label: trashActive 
              ? 'Drop to hide FAB' 
              : (archivedCount > 0 ? `Trash (${archivedCount} hidden FABs) - Drag FABs here to hide` : 'Trash (empty) - Drag FABs here to hide'),
            badge: archivedCount > 0 ? archivedCount : undefined,
          };
        case 'reset':
          return {
            icon: <RestartAltIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonRed }} />,
            color: cyber.neonRed,
            bgColor: getFabBackground(cyber.neonRed, 'soft'),
            onClick: () => setShowResetConfirm(true),
            label: 'Reset FAB Layout',
          };
        // D-Pad Map Pan Controls - draggable FABs for panning the map
        case 'dpadUp':
          return {
            icon: <KeyboardArrowUpIcon sx={{ fontSize: LARGE_ICON_SIZE, color: cyber.neonCyan }} />,
            color: cyber.neonCyan,
            bgColor: getFabBackground(cyber.neonCyan, 'soft'),
            onClick: () => onPan?.('up'),
            label: 'Pan Map Up',
          };
        case 'dpadDown':
          return {
            icon: <KeyboardArrowDownIcon sx={{ fontSize: LARGE_ICON_SIZE, color: cyber.neonCyan }} />,
            color: cyber.neonCyan,
            bgColor: getFabBackground(cyber.neonCyan, 'soft'),
            onClick: () => onPan?.('down'),
            label: 'Pan Map Down',
          };
        case 'dpadLeft':
          return {
            icon: <KeyboardArrowLeftIcon sx={{ fontSize: LARGE_ICON_SIZE, color: cyber.neonCyan }} />,
            color: cyber.neonCyan,
            bgColor: getFabBackground(cyber.neonCyan, 'soft'),
            onClick: () => onPan?.('left'),
            label: 'Pan Map Left',
          };
        case 'dpadRight':
          return {
            icon: <KeyboardArrowRightIcon sx={{ fontSize: LARGE_ICON_SIZE, color: cyber.neonCyan }} />,
            color: cyber.neonCyan,
            bgColor: getFabBackground(cyber.neonCyan, 'soft'),
            onClick: () => onPan?.('right'),
            label: 'Pan Map Right',
          };
        default:
          return null;
      }
    };

    const config = getFabConfig();
    if (!config) return null;

    const handleClick = (e?: React.MouseEvent<HTMLElement>) => {
      if (isDragMode) {
        setIsDragMode(false);
        setDraggedFab(null);
        setDragPosition(null);
        setDragOverIndex(null);
        return;
      }
      // Stop propagation so clicking a FAB doesn't trigger toolbar background click
      e?.stopPropagation();
      telegram.hapticFeedback('medium');
      feedbackService.tap();
      // For trash FAB, pass the event to get anchor element
      if (fabId === 'trash' && e) {
        config.onClick(e);
      } else {
        config.onClick();
      }
      // Don't hide toolbar when clicking FABs - only hide when clicking toolbar background
    };

    // Check if this is the trash FAB and something is being dragged over it
    const isTrashHighlighted = fabId === 'trash' && isOverTrash && draggedFab && draggedFab !== 'trash';

    return (
      <Box
        key={fabId}
        ref={(el: HTMLElement | null) => { if (el) fabRefs.current.set(fabId, el); }}
        sx={{ ...wrapperSx, ...dropIndicatorStyle, position: 'relative' }}
      >
        <Tooltip
          title={config.label}
          placement={
            zone === 'bottom' ? 'top' :
            zone === 'top' ? 'bottom' :
            zone === 'left' ? 'right' : 'left'
          }
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <IconButton
            onClick={handleClick}
            onTouchStart={(e) => handleLongPressStart(fabId, e)}
            onTouchEnd={handleLongPressEnd}
            onMouseDown={(e) => handleLongPressStart(fabId, e)}
            onMouseUp={handleLongPressEnd}
            onMouseLeave={handleLongPressEnd}
            sx={{
              ...fabSx,
              // White background for most FABs in light mode, transparent for AI FAB
              background: config.transparentBg 
                ? 'transparent'
                : darkMode 
                  ? (config.bgColor || `linear-gradient(135deg, ${config.color}20 0%, ${config.color}35 100%)`)
                  : '#ffffff',
              backdropFilter: 'blur(12px)',
              border: isDragMode
                ? `${FAB_BORDER_WIDTH}px dashed ${cyber.neonPurple}`
                : isTrashHighlighted
                  ? `${FAB_BORDER_WIDTH}px solid ${cyber.neonRed}`
                  : darkMode
                    ? `${FAB_BORDER_WIDTH}px solid ${config.color}60`
                    : `${FAB_BORDER_WIDTH}px solid ${config.color}80`,
              boxShadow: isTrashHighlighted
                ? `0 0 20px ${cyber.neonRed}, 0 0 40px ${cyber.neonRed}50`
                : darkMode
                  ? `0 4px 16px ${config.color}40`
                  : `0 4px 16px rgba(0,0,0,0.15), 0 0 0 1px ${config.color}30`,
              animation: isTrashHighlighted ? 'trashPulse 0.5s ease-in-out infinite' : 'none',
              transform: isTrashHighlighted ? 'scale(1.2)' : 'scale(1)',
              '@keyframes trashPulse': {
                '0%, 100%': { 
                  boxShadow: `0 0 20px ${cyber.neonRed}, 0 0 40px ${cyber.neonRed}50`,
                },
                '50%': { 
                  boxShadow: `0 0 30px ${cyber.neonRed}, 0 0 60px ${cyber.neonRed}80`,
                },
              },
              '&:hover': {
                transform: isTrashHighlighted ? 'scale(1.2)' : 'scale(1.08)',
                boxShadow: darkMode
                  ? `0 6px 20px ${config.color}50`
                  : `0 6px 20px rgba(0,0,0,0.2), 0 0 0 1px ${config.color}50`,
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            {config.badge ? (
              <Badge 
                badgeContent={config.badge} 
                max={99}
                sx={makeBadgeSx(cyber.neonPink)}
              >
                {config.icon}
              </Badge>
            ) : (
              config.icon
            )}
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // ============================================================================
  // TOOLBAR SWIPE HANDLERS
  // ============================================================================
  
  // Create swipe handler for a toolbar zone
  const createToolbarSwipeHandlers = (zone: MapFabZone) => {
    let startY = 0;
    let startX = 0;
    let moved = false;
    
    return {
      onTouchStart: (e: React.TouchEvent) => {
        // Don't interfere with FAB dragging
        if (isDragMode || draggedFab) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        moved = false;
      },
      onTouchMove: () => {
        moved = true;
      },
      onTouchEnd: (e: React.TouchEvent) => {
        if (isDragMode || draggedFab) return;
        
        const deltaX = e.changedTouches[0].clientX - startX;
        const deltaY = e.changedTouches[0].clientY - startY;
        
        // Check if swipe was in the correct direction to hide
        if (zone === 'top' && deltaY < -SWIPE_THRESHOLD) {
          toggleToolbar('top', false);
        } else if (zone === 'bottom' && deltaY > SWIPE_THRESHOLD) {
          toggleToolbar('bottom', false);
        } else if (zone === 'left' && deltaX < -SWIPE_THRESHOLD) {
          toggleToolbar('left', false);
        } else if (zone === 'right' && deltaX > SWIPE_THRESHOLD) {
          toggleToolbar('right', false);
        }
      },
      // TAP TO HIDE: If user taps the toolbar background (not a FAB), hide it
      onClick: (e: React.MouseEvent) => {
        // Only hide if clicking the toolbar background, not a FAB button
        const target = e.target as HTMLElement;
        const isToolbarBackground = target.classList.contains('MuiBox-root') || 
                                    target === e.currentTarget;
        if (isToolbarBackground && !moved && !isDragMode && !draggedFab) {
          toggleToolbar(zone, false);
        }
      },
    };
  };

  // ============================================================================
  // ZONE RENDERERS
  // ============================================================================

  const renderTopToolbar = () => {
    const topFabs = layout.top;
    const swipeHandlers = createToolbarSwipeHandlers('top');

    return (
      <Box
        {...swipeHandlers}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: TOOLBAR_STRIP_HEIGHT,
          minHeight: TOOLBAR_STRIP_HEIGHT,
          maxHeight: TOOLBAR_STRIP_HEIGHT + 4,
          pointerEvents: 'auto',
          paddingTop: 'env(safe-area-inset-top, 6px)',
          paddingBottom: '6px',
          paddingLeft: 0,
          paddingRight: 0,
          display: visible ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'flex-start',
          zIndex: 1500,
          background: toolbarSurface.background,
          backdropFilter: 'blur(12px)',
          borderRadius: 3,
          border: dragOverZone === 'top' ? `2px solid ${cyber.neonCyan}60` : toolbarSurface.border,
          boxShadow: dragOverZone === 'top' ? `inset 0 0 30px ${cyber.neonCyan}60, 0 4px 20px ${cyber.neonCyan}40` : toolbarSurface.shadow,
          // Swipe hide/show animation
          transform: toolbarVisibility.top ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, border 0.2s ease',
          overflowY: 'visible',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap', // single row like voice navigation toolbar
          gap: evenTopBottomSpacing ? 1.5 : 0.75,
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: 'max-content',
          minWidth: '100%',
          height: '100%',
          px: 0.75,
        }}>
          {topFabs.map((id, index) => renderFab(id, 'top', index))}
        </Box>
      </Box>
    );
  };

  const renderLeftColumn = () => {
    const leftFabs = layout.left;
    if (leftFabs.length === 0 && !isDragMode) return null;
    const swipeHandlers = createToolbarSwipeHandlers('left');

    return (
      <Box
        {...swipeHandlers}
        sx={{
          position: 'absolute',
          top: VERTICAL_ZONE_TOP_OFFSET,
          left: 0,
          bottom: VERTICAL_ZONE_BOTTOM_OFFSET,
          width: TOOLBAR_COLUMN_WIDTH,
          pointerEvents: 'auto',
          display: visible ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          pl: 0.75,
          pr: 0.75,
          zIndex: 1099,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollSnapType: 'y proximity',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          overscrollBehaviorY: 'contain',
          borderRadius: 3,
          background: toolbarSurface.background,
          backdropFilter: 'blur(12px)',
          boxShadow: dragOverZone === 'left' 
            ? `inset 0 0 30px ${cyber.neonCyan}60, 4px 0 20px ${cyber.neonCyan}40` 
            : toolbarSurface.shadow,
          border: dragOverZone === 'left' ? `2px solid ${cyber.neonCyan}60` : toolbarSurface.border,
          // Swipe hide/show animation
          transform: toolbarVisibility.left ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, border 0.2s ease',
          // Tron roll fade effect for vertical scrolling (same as right toolbar)
          maskImage: leftFabs.length > 6 
            ? 'linear-gradient(to bottom, transparent, black 15px, black calc(100% - 15px), transparent)'
            : 'none',
          WebkitMaskImage: leftFabs.length > 6 
            ? 'linear-gradient(to bottom, transparent, black 15px, black calc(100% - 15px), transparent)'
            : 'none',
        }}
      >
        {leftFabs.map((id, index) => renderFab(id, 'left', index))}
      </Box>
    );
  };

  const renderRightColumn = () => {
    const rightFabs = layout.right;
    const swipeHandlers = createToolbarSwipeHandlers('right');

    return (
      <Box
        {...swipeHandlers}
        sx={{
          position: 'absolute',
          top: VERTICAL_ZONE_TOP_OFFSET,
          right: 0,
          bottom: VERTICAL_ZONE_BOTTOM_OFFSET,
          width: TOOLBAR_COLUMN_WIDTH,
          pointerEvents: 'auto',
          display: visible ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          pl: 0.75,
          pr: 0.75,
          zIndex: 1099,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollSnapType: 'y proximity',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          overscrollBehaviorY: 'contain',
          borderRadius: 3,
          background: toolbarSurface.background,
          backdropFilter: 'blur(12px)',
          boxShadow: dragOverZone === 'right' 
            ? `inset 0 0 30px ${cyber.neonCyan}60, -4px 0 20px ${cyber.neonCyan}40` 
            : toolbarSurface.shadow,
          border: dragOverZone === 'right' ? `2px solid ${cyber.neonCyan}60` : toolbarSurface.border,
          // Swipe hide/show animation
          transform: toolbarVisibility.right ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, border 0.2s ease',
          // Tron roll fade effect for vertical scrolling
          maskImage: rightFabs.length > 6 
            ? 'linear-gradient(to bottom, transparent, black 15px, black calc(100% - 15px), transparent)'
            : 'none',
          WebkitMaskImage: rightFabs.length > 6 
            ? 'linear-gradient(to bottom, transparent, black 15px, black calc(100% - 15px), transparent)'
            : 'none',
        }}
      >
        {rightFabs.map((id, index) => renderFab(id, 'right', index))}
      </Box>
    );
  };

  const renderBottomFooter = () => {
    const bottomFabs = layout.bottom;
    const swipeHandlers = createToolbarSwipeHandlers('bottom');

    return (
      <Box
        {...swipeHandlers}
        sx={{
          position: 'absolute',
          bottom: 12,
          left: 0,
          right: 0,
          height: TOOLBAR_STRIP_HEIGHT,
          minHeight: TOOLBAR_STRIP_HEIGHT,
          maxHeight: TOOLBAR_STRIP_HEIGHT + 4,
          pointerEvents: 'auto',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 4px) + 4px)',
          paddingTop: '6px',
          paddingLeft: 8,
          paddingRight: 8,
          background: toolbarSurface.background,
          backdropFilter: 'blur(12px)',
          borderRadius: 3,
          border: dragOverZone === 'bottom' ? `2px solid ${cyber.neonGreen}60` : toolbarSurface.border,
          display: visible ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1500,
          boxShadow: dragOverZone === 'bottom' ? `inset 0 0 30px ${cyber.neonGreen}60, 0 -4px 20px ${cyber.neonGreen}40` : toolbarSurface.shadow,
          // Swipe hide/show animation
          transform: toolbarVisibility.bottom ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, border 0.2s ease',
          overflowY: 'visible',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          maskImage: bottomFabs.length > 5 
            ? 'linear-gradient(to right, transparent, black 20px, black calc(100% - 20px), transparent)'
            : 'none',
          WebkitMaskImage: bottomFabs.length > 5 
            ? 'linear-gradient(to right, transparent, black 20px, black calc(100% - 20px), transparent)'
            : 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap', // single row like voice navigation toolbar
            gap: evenTopBottomSpacing ? 1.5 : 0.75,
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: 'max-content',
            minWidth: '100%',
            height: '100%',
            pl: 0,
            pr: 0,
          }}
        >
          {bottomFabs.map((id, index) => renderFab(id, 'bottom', index))}
        </Box>
      </Box>
    );
  };

  // ============================================================================
  // DRAG GHOST ICON
  // ============================================================================

  const getDraggedFabIcon = () => {
    if (!draggedFab) return null;

    const iconMap: Record<string, ReactNode> = {
      home: <HomeIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonCyan }} />,
      search: <SearchIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonCyan }} />,
      voice: <RecordVoiceOverIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonBlue }} />,
      // manualSearch: <SearchIcon sx={{ fontSize: ICON_SIZE, color: '#e2e8f0' }} />,
      gpsToggle: <GpsFixedIcon sx={{ fontSize: ICON_SIZE, color: locationStateColor }} />,
      ai: <span style={{ fontSize: ICON_SIZE * 0.7, fontWeight: 800, color: cyber.neonCyan, fontFamily: 'monospace' }}>AI</span>,
      zoomIn: <AddIcon sx={{ fontSize: LARGE_ICON_SIZE - 4, color: cyber.neonCyan }} />,
      zoomOut: <RemoveIcon sx={{ fontSize: LARGE_ICON_SIZE - 4, color: cyber.neonCyan }} />,
      layers: <LayersIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonPurple }} />,
      facilityLayer: <FilterListIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonOrange }} />,
      myLocation: <MyLocationIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonCyan }} />,
      weather: <CloudIcon sx={{ fontSize: ICON_SIZE, color: '#60a5fa' }} />,
      wind: <AirIcon sx={{ fontSize: ICON_SIZE, color: '#22d3ee' }} />,
      rain: <WaterDropIcon sx={{ fontSize: ICON_SIZE, color: '#3b82f6' }} />,
      settings: <SettingsIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonPurple }} />,
      routesToggle: <RouteIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonCyan }} />,
      landmark: <FlagIcon sx={{ fontSize: ICON_SIZE, color: '#22c55e' }} />,
      reset: <RestartAltIcon sx={{ fontSize: ICON_SIZE, color: '#ef4444' }} />,
      safetyGuy: <LocalPoliceIcon sx={{ fontSize: ICON_SIZE, color: '#ec4899' }} />,
      // D-pad FABs for map panning
      dpadUp: <KeyboardArrowUpIcon sx={{ fontSize: LARGE_ICON_SIZE, color: cyber.neonCyan }} />,
      dpadDown: <KeyboardArrowDownIcon sx={{ fontSize: LARGE_ICON_SIZE, color: cyber.neonCyan }} />,
      dpadLeft: <KeyboardArrowLeftIcon sx={{ fontSize: LARGE_ICON_SIZE, color: cyber.neonCyan }} />,
      dpadRight: <KeyboardArrowRightIcon sx={{ fontSize: LARGE_ICON_SIZE, color: cyber.neonCyan }} />,
    };

    return iconMap[draggedFab] || null;
  };

  const ghostFabSize = draggedFab && DIRECTIONAL_FAB_IDS.has(draggedFab)
    ? LARGE_FAB_SIZE
    : FAB_SIZE;

  const arrowAccentTop = arrowAccents.top;
  const arrowAccentBottom = arrowAccents.bottom;
  const arrowAccentLeft = arrowAccents.left;
  const arrowAccentRight = arrowAccents.right;

  // ============================================================================
  // D-PAD COMPONENT - Map panning controls (deprecated, now FABs)
  // ============================================================================
  // D-pad arrows are now converted to draggable FABs (dpadUp, dpadDown, dpadLeft, dpadRight)
  // They can be added/removed and moved between zones like other FABs
  // See cases 'dpadUp', 'dpadDown', 'dpadLeft', 'dpadRight' in getFabConfig for their configuration

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
    >
      {renderTopToolbar()}
      {renderLeftColumn()}
      {renderRightColumn()}
      {/* D-pad now rendered as FABs via MapFabManager - no fixed D-pad component */}
      {/* {renderDpad()} */}
      {renderBottomFooter()}

      {/* ============================================================ */}
      {/* HIDDEN TOOLBAR INDICATORS & SHOW ALL BUTTON                   */}
      {/* ============================================================ */}

      {/* Top edge arrow indicator when top toolbar is hidden */}
      {!toolbarVisibility.top && visible && (
        <Box
          onClick={() => toggleToolbar('top', true)}
          sx={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: ARROW_INDICATOR_SIZE,
            height: ARROW_INDICATOR_SIZE,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${withAlpha(arrowAccentTop, 0.25)} 0%, ${withAlpha(arrowAccentTop, 0.55)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `2px solid ${withAlpha(arrowAccentTop, 0.7)}`,
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 18px ${withAlpha(arrowAccentTop, 0.35)}, 0 0 18px ${withAlpha(arrowAccentTop, 0.45)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'bounceDown 1.5s ease-in-out infinite',
            '@keyframes bounceDown': {
              '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
              '50%': { transform: 'translateX(-50%) translateY(6px)' },
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${withAlpha(arrowAccentTop, 0.4)} 0%, ${withAlpha(arrowAccentTop, 0.7)} 100%)`,
              borderColor: arrowAccentTop,
              boxShadow: `0 6px 24px ${withAlpha(arrowAccentTop, 0.4)}, 0 0 26px ${withAlpha(arrowAccentTop, 0.6)}`,
            },
            '&:active': {
              transform: 'translateX(-50%) scale(0.92)',
              animation: 'none',
            },
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: ARROW_ICON_SIZE, color: arrowAccentTop }} />
        </Box>
      )}

      {/* Bottom edge arrow indicator when bottom toolbar is hidden */}
      {!toolbarVisibility.bottom && visible && (
        <Box
          onClick={() => toggleToolbar('bottom', true)}
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: ARROW_INDICATOR_SIZE,
            height: ARROW_INDICATOR_SIZE,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${withAlpha(arrowAccentBottom, 0.25)} 0%, ${withAlpha(arrowAccentBottom, 0.55)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `2px solid ${withAlpha(arrowAccentBottom, 0.7)}`,
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 18px ${withAlpha(arrowAccentBottom, 0.35)}, 0 0 18px ${withAlpha(arrowAccentBottom, 0.45)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'bounceUp 1.5s ease-in-out infinite',
            '@keyframes bounceUp': {
              '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
              '50%': { transform: 'translateX(-50%) translateY(-6px)' },
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${withAlpha(arrowAccentBottom, 0.4)} 0%, ${withAlpha(arrowAccentBottom, 0.7)} 100%)`,
              borderColor: arrowAccentBottom,
              boxShadow: `0 6px 24px ${withAlpha(arrowAccentBottom, 0.4)}, 0 0 26px ${withAlpha(arrowAccentBottom, 0.6)}`,
            },
            '&:active': {
              transform: 'translateX(-50%) scale(0.92)',
              animation: 'none',
            },
          }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: ARROW_ICON_SIZE, color: arrowAccentBottom }} />
        </Box>
      )}

      {/* Left edge arrow indicator when left toolbar is hidden */}
      {!toolbarVisibility.left && visible && (
        <Box
          onClick={() => toggleToolbar('left', true)}
          sx={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: ARROW_INDICATOR_SIZE,
            height: ARROW_INDICATOR_SIZE,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${withAlpha(arrowAccentLeft, 0.25)} 0%, ${withAlpha(arrowAccentLeft, 0.55)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `2px solid ${withAlpha(arrowAccentLeft, 0.7)}`,
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 18px ${withAlpha(arrowAccentLeft, 0.35)}, 0 0 18px ${withAlpha(arrowAccentLeft, 0.45)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'bounceRight 1.5s ease-in-out infinite',
            '@keyframes bounceRight': {
              '0%, 100%': { transform: 'translateY(-50%) translateX(0)' },
              '50%': { transform: 'translateY(-50%) translateX(6px)' },
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${withAlpha(arrowAccentLeft, 0.4)} 0%, ${withAlpha(arrowAccentLeft, 0.7)} 100%)`,
              borderColor: arrowAccentLeft,
              boxShadow: `0 6px 24px ${withAlpha(arrowAccentLeft, 0.4)}, 0 0 26px ${withAlpha(arrowAccentLeft, 0.6)}`,
            },
            '&:active': {
              transform: 'translateY(-50%) scale(0.92)',
              animation: 'none',
            },
          }}
        >
          <KeyboardArrowRightIcon sx={{ fontSize: ARROW_ICON_SIZE, color: arrowAccentLeft }} />
        </Box>
      )}

      {/* Right edge arrow indicator when right toolbar is hidden */}
      {!toolbarVisibility.right && visible && (
        <Box
          onClick={() => toggleToolbar('right', true)}
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: ARROW_INDICATOR_SIZE,
            height: ARROW_INDICATOR_SIZE,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${withAlpha(arrowAccentRight, 0.25)} 0%, ${withAlpha(arrowAccentRight, 0.55)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `2px solid ${withAlpha(arrowAccentRight, 0.7)}`,
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 18px ${withAlpha(arrowAccentRight, 0.35)}, 0 0 18px ${withAlpha(arrowAccentRight, 0.45)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'bounceLeft 1.5s ease-in-out infinite',
            '@keyframes bounceLeft': {
              '0%, 100%': { transform: 'translateY(-50%) translateX(0)' },
              '50%': { transform: 'translateY(-50%) translateX(-6px)' },
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${withAlpha(arrowAccentRight, 0.4)} 0%, ${withAlpha(arrowAccentRight, 0.7)} 100%)`,
              borderColor: arrowAccentRight,
              boxShadow: `0 6px 24px ${withAlpha(arrowAccentRight, 0.4)}, 0 0 26px ${withAlpha(arrowAccentRight, 0.6)}`,
            },
            '&:active': {
              transform: 'translateY(-50%) scale(0.92)',
              animation: 'none',
            },
          }}
        >
          <KeyboardArrowLeftIcon sx={{ fontSize: ARROW_ICON_SIZE, color: arrowAccentRight }} />
        </Box>
      )}

      {/* Show All Toolbars floating button - appears when any toolbar is hidden */}
      {anyToolbarHidden && visible && !isDragMode && (
        <Box
          onClick={showAllToolbars}
          sx={{
            position: 'absolute',
            bottom: toolbarVisibility.bottom ? 96 : 90, // Move up to avoid zoom + button
            right: toolbarVisibility.right ? 80 : 16,
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: `${cyber.bgCard}95`,
            backdropFilter: 'blur(10px)',
            border: `2px solid ${cyber.neonCyan}60`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1150,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: `0 4px 20px rgba(0,0,0,0.3), 0 0 15px ${cyber.neonCyan}30`,
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: `0 4px 25px rgba(0,0,0,0.4), 0 0 25px ${cyber.neonCyan}50`,
              borderColor: cyber.neonCyan,
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <VisibilityIcon sx={{ fontSize: 22, color: '#3b82f6' }} />
        </Box>
      )}

      {/* Drag mode overlay with ghost FAB */}
      {isDragMode && (
        <>
          {/* Dark overlay */}
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1200,
              pointerEvents: 'none',
            }}
          />

          {/* Instruction text */}
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1201,
              pointerEvents: 'none',
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                color: cyber.textPrimary,
                fontSize: '1.1rem',
                fontWeight: 600,
                textShadow: `0 0 20px ${cyber.neonCyan}`,
                mb: 1,
              }}
            >
              Drag to a toolbar zone
            </Typography>
            <Typography
              sx={{
                color: cyber.textSecondary,
                fontSize: '0.9rem',
              }}
            >
              {dragOverZone === 'top' ? '📍 Top Toolbar' :
               dragOverZone === 'left' ? '📍 Left Column' :
               dragOverZone === 'right' ? '📍 Right Column' :
               dragOverZone === 'bottom' ? '📍 Bottom Bar' :
               'Move to a highlighted zone'}
            </Typography>
          </Box>

          {/* Ghost FAB following cursor */}
          {dragPosition && (
            <Box
              sx={{
                position: 'fixed',
                left: dragPosition.x - ghostFabSize / 2,
                top: dragPosition.y - ghostFabSize / 2,
                width: ghostFabSize,
                height: ghostFabSize,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${cyber.neonPurple}40 0%, ${cyber.neonCyan}40 100%)`,
                border: `3px solid ${cyber.neonPurple}`,
                boxShadow: `0 0 30px ${cyber.neonPurple}80, 0 0 60px ${cyber.neonCyan}40`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1202,
                pointerEvents: 'none',
                animation: 'pulse 1s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                },
              }}
            >
              {getDraggedFabIcon()}
            </Box>
          )}
        </>
      )}

      {/* GPS Location SpeedDial Menu */}
      {showGpsMenu && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            zIndex: 1200,
            animation: 'slideUp 0.2s ease-out',
            '@keyframes slideUp': {
              from: { opacity: 0, transform: 'translateX(-50%) translateY(20px)' },
              to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
            },
          }}
        >
          {/* GPS Location Toggle
              - Individual users: can toggle ON/OFF freely
              - Business Plus crew members: GPS is LOCKED ON, cannot turn off */}
          <Box
            onClick={async (e) => {
              e.stopPropagation();
              
              // Crew members cannot turn off GPS - it's required by Business Plus
              if (isCrewMember) {
                telegram.hapticFeedback('error');
                telegram.showPopup({
                  title: '🔒 GPS Required',
                  message: crewName 
                    ? `As a member of "${crewName}", GPS location sharing is required and cannot be disabled.`
                    : 'As a Business Plus crew member, GPS location sharing is required and cannot be disabled.',
                  buttons: [{ id: 'ok', type: 'ok', text: 'Understood' }]
                });
                setShowGpsMenu(false);
                return;
              }
              
              telegram.hapticFeedback('medium');
              
              try {
                if (!personalLocationEnabled && onRequestLocation) {
                  await onRequestLocation();
                }
                if (onTogglePersonalLocation) {
                  onTogglePersonalLocation();
                }
              } catch (err) {
                console.error('[GPS] Error toggling location:', err);
              }
              
              setShowGpsMenu(false);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              padding: '12px 20px',
              borderRadius: 3,
              backgroundColor: darkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              border: isCrewMember 
                ? '3px solid #f59e0b' 
                : `3px solid ${personalLocationEnabled ? '#10b981' : '#ef4444'}`,
              boxShadow: isCrewMember 
                ? '0 4px 20px #f59e0b40' 
                : `0 4px 20px ${personalLocationEnabled ? '#10b98140' : '#ef444440'}`,
              cursor: isCrewMember ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: isCrewMember ? 0.9 : 1,
              '&:hover': {
                transform: isCrewMember ? 'none' : 'scale(1.02)',
                boxShadow: isCrewMember 
                  ? '0 4px 20px #f59e0b40' 
                  : `0 6px 25px ${personalLocationEnabled ? '#10b98160' : '#ef444460'}`,
              },
              '&:active': {
                transform: isCrewMember ? 'none' : 'scale(0.98)',
              },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isCrewMember ? '#f59e0b20' : (personalLocationEnabled ? '#10b98120' : '#ef444420'),
                border: isCrewMember ? '2px solid #f59e0b' : `2px solid ${personalLocationEnabled ? '#10b981' : '#ef4444'}`,
                position: 'relative',
              }}
            >
              {isCrewMember ? (
                <>
                  <MyLocationIcon sx={{ fontSize: 24, color: '#f59e0b' }} />
                  <LockIcon 
                    sx={{ 
                      position: 'absolute',
                      fontSize: 12,
                      color: '#ef4444',
                      bottom: -2,
                      right: -2,
                      backgroundColor: darkMode ? '#1e293b' : '#fff',
                      borderRadius: '50%',
                      padding: '2px',
                    }} 
                  />
                </>
              ) : personalLocationEnabled
                ? <MyLocationIcon sx={{ fontSize: 24, color: '#10b981' }} />
                : <LocationOffIcon sx={{ fontSize: 24, color: '#ef4444' }} />}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: cyber.textPrimary }}>
                {isCrewMember ? '🔒 GPS Required' : (personalLocationEnabled ? 'My Location ON' : 'My Location OFF')}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: cyber.textSecondary, opacity: 0.8 }}>
                {isCrewMember 
                  ? (crewName ? `Locked by ${crewName}` : 'Required by crew')
                  : `Tap to ${personalLocationEnabled ? 'disable' : 'enable'}`}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: locationStateColor,
                marginLeft: 'auto',
                ...(locationStatus === 'active' && {
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 0 0 0 #10b98180' },
                    '70%': { boxShadow: '0 0 0 6px #10b98100' },
                    '100%': { boxShadow: '0 0 0 0 #10b98100' },
                  },
                }),
                ...(locationStatus === 'pending' && {
                  animation: 'blink 1s infinite',
                  '@keyframes blink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                  },
                }),
              }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: '0.7rem',
              color: cyber.textSecondary,
              textAlign: 'center',
              opacity: 0.6,
              mt: 0.5,
            }}
          >
            Tap outside to close
          </Typography>
        </Box>
      )}

      {/* Backdrop for GPS menu */}
      {showGpsMenu && (
        <Box
          onClick={() => {
            telegram.hapticFeedback('light');
            setShowGpsMenu(false);
          }}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1199,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }}
        />
      )}

      {/* Reset confirmation dialog */}
      {showResetConfirm && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => { telegram.hapticFeedback('light'); setShowResetConfirm(false); }}
        >
          <Box
            sx={{
              backgroundColor: cyber.bgCard,
              border: `2px solid ${cyber.neonCyan}`,
              borderRadius: 3,
              p: 3,
              maxWidth: 320,
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, mb: 2 }}>
              Reset FAB Layout?
            </Typography>
            <Typography sx={{ color: cyber.textSecondary, fontSize: '0.9rem', mb: 3 }}>
              This will restore all buttons to their default positions.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <IconButton
                onClick={() => { telegram.hapticFeedback('light'); setShowResetConfirm(false); }}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: cyber.bgSecondary,
                  color: cyber.textPrimary,
                  border: `1px solid ${cyber.borderDefault}`,
                }}
              >
                Cancel
              </IconButton>
              <IconButton
                onClick={resetLayout}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#dc2626' },
                }}
              >
                Reset
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* Settings SpeedDial Menu */}
      {showSettingsMenu && (
        <>
          {/* Backdrop - clicks outside mini FABs close the menu */}
          <Box
            onClick={closeSettingsMenu}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1999,
              backgroundColor: 'transparent',
              pointerEvents: 'auto',
            }}
          />
          {/* Mini FABs container - must be AFTER backdrop so it renders on top */}
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'fixed',
              bottom: 110,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              zIndex: 2000,
              pointerEvents: 'auto',
              animation: 'slideUp 0.2s ease-out',
              '@keyframes slideUp': {
                from: { opacity: 0, transform: 'translateX(-50%) translateY(20px)' },
                to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
              },
            }}
          >
            <Tooltip title="Help & Support" arrow placement="top">
              <IconButton
                onClick={() => {
                  telegram.hapticFeedback('light');
                  // Open Telegram support bot
                  window.open('https://t.me/ziawellnavsupportbot', '_blank');
                  closeSettingsMenu();
                }}
                sx={{
                  width: 52,
                  height: 52,
                  background: darkMode 
                    ? `linear-gradient(135deg, ${cyber.neonPink}20 0%, ${cyber.neonPink}40 100%)`
                    : '#ffffff',
                  border: `3px solid ${cyber.neonPink}`,
                  boxShadow: darkMode 
                    ? `0 0 15px ${cyber.neonPink}60`
                    : `0 4px 12px rgba(0,0,0,0.15)`,
                  color: cyber.neonPink,
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: darkMode 
                      ? `0 0 25px ${cyber.neonPink}80`
                      : `0 6px 16px rgba(0,0,0,0.2)`,
                  },
                }}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Offline Maps" arrow placement="top">
              <IconButton
                onClick={() => {
                  telegram.hapticFeedback('light');
                  onOpenOfflineCache?.();
                  closeSettingsMenu();
                }}
                sx={{
                  width: 52,
                  height: 52,
                  background: darkMode 
                    ? `linear-gradient(135deg, ${cyber.neonCyan}20 0%, ${cyber.neonCyan}40 100%)`
                    : '#ffffff',
                  border: `3px solid ${cyber.neonCyan}`,
                  boxShadow: darkMode 
                    ? `0 0 15px ${cyber.neonCyan}60`
                    : `0 4px 12px rgba(0,0,0,0.15)`,
                  color: cyber.neonCyan,
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: darkMode 
                      ? `0 0 25px ${cyber.neonCyan}80`
                      : `0 6px 16px rgba(0,0,0,0.2)`,
                  },
                }}
              >
                <CloudDownloadIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={darkMode ? 'Light Mode' : 'Dark Mode'} arrow placement="top">
              <IconButton
                onClick={() => {
                  telegram.hapticFeedback('light');
                  onToggleDarkMode?.();
                  closeSettingsMenu();
                }}
                sx={{
                  width: 52,
                  height: 52,
                  background: darkMode 
                    ? `linear-gradient(135deg, ${cyber.neonYellow}20 0%, ${cyber.neonYellow}40 100%)`
                    : '#ffffff',
                  border: `3px solid ${cyber.neonYellow}`,
                  boxShadow: darkMode 
                    ? `0 0 15px ${cyber.neonYellow}60`
                    : `0 4px 12px rgba(0,0,0,0.15)`,
                  color: cyber.neonYellow,
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: darkMode 
                      ? `0 0 25px ${cyber.neonYellow}80`
                      : `0 6px 16px rgba(0,0,0,0.2)`,
                  },
                }}
              >
                {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title={highContrast ? 'Normal Contrast' : 'High Contrast'} arrow placement="top">
              <IconButton
                onClick={() => {
                  telegram.hapticFeedback('light');
                  onToggleHighContrast?.();
                  closeSettingsMenu();
                }}
                sx={{
                  width: 52,
                  height: 52,
                  background: darkMode
                    ? (highContrast
                        ? `linear-gradient(135deg, ${cyber.neonGreen}20 0%, ${cyber.neonGreen}40 100%)`
                        : `linear-gradient(135deg, rgba(100,100,100,0.2) 0%, rgba(100,100,100,0.4) 100%)`)
                    : '#ffffff',
                  border: `3px solid ${highContrast ? cyber.neonGreen : '#6b7280'}`,
                  boxShadow: darkMode 
                    ? `0 0 15px ${highContrast ? cyber.neonGreen : '#6b7280'}60`
                    : `0 4px 12px rgba(0,0,0,0.15)`,
                  color: highContrast ? cyber.neonGreen : '#6b7280',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: darkMode 
                      ? `0 0 25px ${highContrast ? cyber.neonGreen : '#6b7280'}80`
                      : `0 6px 16px rgba(0,0,0,0.2)`,
                  },
                }}
              >
                <ContrastIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={soundEnabled ? 'Mute Sounds' : 'Enable Sounds'} arrow placement="top">
              <IconButton
                onClick={() => {
                  telegram.hapticFeedback('light');
                  onToggleSound?.();
                  closeSettingsMenu();
                }}
                sx={{
                  width: 52,
                  height: 52,
                  background: darkMode
                    ? (soundEnabled
                        ? `linear-gradient(135deg, ${cyber.neonBlue}20 0%, ${cyber.neonBlue}40 100%)`
                        : `linear-gradient(135deg, rgba(100,100,100,0.2) 0%, rgba(100,100,100,0.4) 100%)`)
                    : '#ffffff',
                  border: `3px solid ${soundEnabled ? cyber.neonBlue : '#6b7280'}`,
                  boxShadow: darkMode 
                    ? `0 0 15px ${soundEnabled ? cyber.neonBlue : '#6b7280'}60`
                    : `0 4px 12px rgba(0,0,0,0.15)`,
                  color: soundEnabled ? cyber.neonBlue : '#6b7280',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: darkMode 
                      ? `0 0 25px ${soundEnabled ? cyber.neonBlue : '#6b7280'}80`
                      : `0 6px 16px rgba(0,0,0,0.2)`,
                  },
                }}
              >
                {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      {/* Trash Menu - shows archived/hidden FABs */}
      <Menu
        anchorEl={trashMenuAnchor}
        open={Boolean(trashMenuAnchor)}
        onClose={() => { telegram.hapticFeedback('light'); setTrashMenuAnchor(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${cyber.neonRed}40`,
            borderRadius: 2,
            minWidth: 200,
            maxHeight: 300,
          }
        }}
      >
        <Typography sx={{ 
          px: 2, 
          py: 1, 
          color: cyber.neonRed, 
          fontWeight: 600,
          fontSize: '0.85rem',
          borderBottom: `1px solid ${cyber.borderDefault}`,
        }}>
          🗑️ Hidden FABs
        </Typography>
        {(layout.archived?.length || 0) === 0 ? (
          <MenuItem disabled sx={{ color: cyber.textMuted, fontSize: '0.85rem' }}>
            No hidden FABs
          </MenuItem>
        ) : (
          layout.archived?.map(fabId => (
            <MenuItem 
              key={fabId}
              onClick={() => { telegram.hapticFeedback('medium'); feedbackService.playSound('success'); restoreFab(fabId); }}
              sx={{ 
                color: cyber.textPrimary,
                '&:hover': { backgroundColor: `${cyber.neonCyan}20` },
              }}
            >
              <ListItemIcon>
                <RestoreIcon sx={{ color: cyber.neonCyan, fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary={getFabLabel(fabId)}
                primaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </MenuItem>
          ))
        )}
        <Divider sx={{ borderColor: cyber.borderDefault, my: 0.5 }} />
        
        {/* Spacer management */}
        <Typography sx={{ 
          px: 2, 
          py: 0.5, 
          color: cyber.neonPurple, 
          fontWeight: 600,
          fontSize: '0.75rem',
        }}>
          ✨ Spacers
        </Typography>
        <MenuItem 
          onClick={() => {
            telegram.hapticFeedback('medium');
            feedbackService.playSound('success');
            // Generate unique spacer ID
            const existingSpacers = [
              ...(layout.top || []),
              ...(layout.bottom || []),
              ...(layout.left || []),
              ...(layout.right || []),
            ].filter(id => id.startsWith('spacer_'));
            const spacerNum = existingSpacers.length + 1;
            const newSpacerId = `spacer_${spacerNum}_${Date.now()}`;
            // Add spacer to the current zone (default to bottom)
            setLayout(prev => {
              const newLayout = { ...prev };
              newLayout.bottom = [...(prev.bottom || []), newSpacerId];
              return newLayout;
            });
            setToast({ open: true, message: 'Spacer added to bottom toolbar. Long-press to drag it anywhere!', severity: 'success' });
            setTrashMenuAnchor(null);
          }}
          sx={{ 
            color: cyber.neonPurple,
            '&:hover': { backgroundColor: `${cyber.neonPurple}20` },
          }}
        >
          <ListItemIcon>
            <Typography sx={{ fontSize: 16, color: cyber.neonPurple }}>➕</Typography>
          </ListItemIcon>
          <ListItemText 
            primary="Add Spacer"
            secondary="Creates gap between FABs"
            primaryTypographyProps={{ fontSize: '0.85rem' }}
            secondaryTypographyProps={{ fontSize: '0.7rem', color: cyber.textMuted }}
          />
        </MenuItem>
        <MenuItem 
          onClick={() => {
            telegram.hapticFeedback('medium');
            // Remove all spacers from all zones
            setLayout(prev => ({
              ...prev,
              top: (prev.top || []).filter(id => !id.startsWith('spacer_')),
              bottom: (prev.bottom || []).filter(id => !id.startsWith('spacer_')),
              left: (prev.left || []).filter(id => !id.startsWith('spacer_')),
              right: (prev.right || []).filter(id => !id.startsWith('spacer_')),
            }));
            setToast({ open: true, message: 'All spacers removed', severity: 'info' });
            setTrashMenuAnchor(null);
          }}
          sx={{ 
            color: cyber.textMuted,
            '&:hover': { backgroundColor: `${cyber.neonPurple}15` },
          }}
        >
          <ListItemIcon>
            <Typography sx={{ fontSize: 16, color: cyber.textMuted }}>🧹</Typography>
          </ListItemIcon>
          <ListItemText 
            primary="Remove All Spacers"
            primaryTypographyProps={{ fontSize: '0.85rem' }}
          />
        </MenuItem>
        <MenuItem 
          onClick={() => {
            telegram.hapticFeedback('light');
            setEvenTopBottomSpacing(prev => {
              const next = !prev;
              setToast({ open: true, message: next ? 'Edge spacing on (top/bottom spread out)' : 'Edge spacing off', severity: 'info' });
              return next;
            });
          }}
          sx={{ 
            color: cyber.textPrimary,
            '&:hover': { backgroundColor: `${cyber.neonPurple}12` },
          }}
        >
          <ListItemIcon>
            <SpaceBarIcon sx={{ color: cyber.neonPurple, fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Edge spacing (top/bottom)"
            secondary="Spread FABS across the bar without spacers"
            primaryTypographyProps={{ fontSize: '0.85rem' }}
            secondaryTypographyProps={{ fontSize: '0.7rem', color: cyber.textMuted }}
          />
          <Switch
            edge="end"
            checked={evenTopBottomSpacing}
            onChange={() => {
              setEvenTopBottomSpacing(prev => {
                const next = !prev;
                setToast({ open: true, message: next ? 'Edge spacing on (top/bottom spread out)' : 'Edge spacing off', severity: 'info' });
                return next;
              });
            }}
          />
        </MenuItem>
        
        <Divider sx={{ borderColor: cyber.borderDefault, my: 0.5 }} />
        <MenuItem 
          onClick={() => {
            telegram.hapticFeedback('medium');
            setTrashMenuAnchor(null);
            setShowResetConfirm(true);
          }}
          sx={{ 
            color: '#ef4444',
            '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.15)' },
          }}
        >
          <ListItemIcon>
            <RestartAltIcon sx={{ color: '#ef4444', fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Reset All FABs"
            primaryTypographyProps={{ fontSize: '0.85rem' }}
          />
        </MenuItem>
      </Menu>

      {/* Toast notifications for spacer actions */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 16, sm: 24 }, zIndex: 10000 }}
      >
        <Alert 
          severity={toast.severity} 
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          sx={{
            backgroundColor: cyber.bgCard,
            border: `1px solid ${cyber.borderDefault}`,
            color: cyber.textPrimary,
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MapFabManager;
